import React, { useState, useEffect, useCallback, useRef, useMemo, startTransition } from "react";
import { flushSync } from "react-dom";
import {
	Container,
	Grid,
	Typography,
	Box,
	Paper,
	Pagination,
	CircularProgress,
	LinearProgress,
	Chip,
	MenuItem,
	Button,
	Stack,
	ThemeProvider,
	List,
	ListItem,
	ListItemText,
	TextField,
	Alert,
	createTheme,
	Fade,
	Menu,
	IconButton,
	Tooltip,
	Popover,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	FormGroup,
	FormControlLabel,
	Checkbox,
	ToggleButtonGroup,
	ToggleButton,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon } from "@mui/icons-material";
import ProductCard from "./ProductCard/ProductCard";
import UploadPage from "./UploadPage/UploadPage";
import ColorContributionRamp, {
	getProductColorHex,
	colorContributionRowMapKey,
	colorContributionRampBucketKey,
} from "./ColorContributionRamp";
import FashionabilityContributionRamp, {
	fashionabilityContributionRowKey,
	fashionabilityContributionMapKey,
} from "./FashionabilityContributionRamp";
import { format } from "date-fns";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { pythonVmInstance } from "utils/AxiosInstances/RareboxPythonInstance";
// Create custom theme
const theme = createTheme({
	typography: {
		fontFamily:
			'"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
		h1: { fontSize: "1.75rem", fontWeight: 600 },
		h2: { fontSize: "1.5rem", fontWeight: 600 },
		h3: { fontSize: "1.25rem", fontWeight: 600 },
		h4: { fontSize: "1.125rem", fontWeight: 600 },
		h5: { fontSize: "0.9375rem", fontWeight: 600 },
		h6: { fontSize: "0.9375rem", fontWeight: 600 },
		body1: { fontSize: "0.8125rem" },
		body2: { fontSize: "0.6875rem" },
		subtitle1: { fontSize: "0.8125rem" },
		subtitle2: { fontSize: "0.6875rem" },
		caption: { fontSize: "0.6875rem" },
		button: { fontSize: "0.6875rem", fontWeight: 500 },
	},
	shape: {
		borderRadius: 12, // Softer round corners for a modern minimalist look
	},
	palette: {
		primary: { main: "#1a1a1a" },
		secondary: { main: "#f5f5f5" },
		background: {
			default: "#fcfcfc",
			paper: "#ffffff",
		},
		text: {
			primary: "#1a1a1a",
			secondary: "#666666",
		},
		action: {
			hover: "rgba(0, 0, 0, 0.04)",
			selected: "rgba(0, 0, 0, 0.08)",
		},
	},
	components: {
		MuiButton: {
			styleOverrides: {
				root: {
					borderRadius: 8,
					textTransform: "none",
					boxShadow: "none",
					"&:hover": {
						boxShadow: "none",
					},
				},
				contained: {
					backgroundColor: "#1a1a1a",
					color: "#fff",
					"&:hover": {
						backgroundColor: "#333",
					},
				},
				outlined: {
					borderColor: "#e0e0e0",
					color: "#1a1a1a",
					"&:hover": {
						borderColor: "#1a1a1a",
						backgroundColor: "rgba(0,0,0,0.02)",
					},
				},
			},
		},
		MuiPaper: {
			styleOverrides: {
				root: {
					backgroundImage: "none",
					boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.04)", // Very subtle shadow instead of heavy border
					border: "1px solid #f0f0f0", // Much softer border
				},
				elevation1: {
					boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.04)",
				},
			},
		},
		MuiCard: {
			styleOverrides: {
				root: {
					boxShadow: "none",
					border: "1px solid #f0f0f0",
					transition: "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
					"&:hover": {
						transform: "translateY(-2px)",
						boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.05)",
					},
				},
			},
		},
		MuiSelect: {
			styleOverrides: {
				root: {
					borderRadius: 8,
					backgroundColor: "#fafafa",
					"&:hover": {
						backgroundColor: "#f5f5f5",
					},
				},
			},
		},
		MuiOutlinedInput: {
			styleOverrides: {
				root: {
					borderRadius: 8,
					backgroundColor: "#fafafa",
					"& .MuiOutlinedInput-notchedOutline": {
						borderColor: "transparent",
					},
					"&:hover .MuiOutlinedInput-notchedOutline": {
						borderColor: "#e0e0e0",
					},
					"&.Mui-focused .MuiOutlinedInput-notchedOutline": {
						borderColor: "#1a1a1a",
						borderWidth: "1px",
					},
				},
			},
		},
		MuiInputLabel: {
			styleOverrides: {
				root: {
					"&.Mui-focused": {
						color: "#1a1a1a",
					},
				},
			},
		},
		MuiToggleButton: {
			styleOverrides: {
				root: {
					border: "1px solid #f0f0f0",
					"&.Mui-selected": {
						backgroundColor: "#1a1a1a !important",
						color: "#ffffff !important",
					},
				},
			},
		},
		MuiChip: {
			styleOverrides: {
				root: {
					borderRadius: 6,
					backgroundColor: "#f5f5f5",
				},
				outlined: {
					borderColor: "#e0e0e0",
					backgroundColor: "transparent",
				},
			},
		},
	},
});

// Lightweight search input: local state for instant typing, native input to avoid MUI overhead
const FilterSearchInput = React.memo(function FilterSearchInput({ field, label, value, onSearchChangeDebounced, sx }) {
	const [localValue, setLocalValue] = useState(value || "");
	useEffect(() => {
		setLocalValue(value || "");
	}, [value]);
	const handleChange = (e) => {
		const v = e.target.value;
		setLocalValue(v);
		onSearchChangeDebounced(field, v);
	};
	return (
		<input
			type="text"
			placeholder={`Search ${label.toLowerCase()}...`}
			value={localValue}
			onChange={handleChange}
			style={{
				width: "100%",
				boxSizing: "border-box",
				fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
				fontSize: "0.6rem",
				fontWeight: 400,
				color: "#666666",
				height: "32px",
				padding: "4px 8px",
				border: "1px solid #e0e0e0",
				borderRadius: "4px",
				backgroundColor: "#fff",
				outline: "none",
			}}
			onFocus={(e) => (e.target.style.borderColor = "#1976d2")}
			onBlur={(e) => (e.target.style.borderColor = "#e0e0e0")}
		/>
	);
});

/** Hover the ⓘ control next to downloads for the full list of what PDF export contains */
const PictorialStPdfExportTooltipTitle = () => (
	<Box sx={{ py: 0.25, maxWidth: 380 }}>
		<Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.75 }}>
			What the PDF includes
		</Typography>
		<Typography variant="caption" component="div" sx={{ display: "block", mb: 1, lineHeight: 1.5 }}>
			<strong>Page 1 (cover):</strong> title banner, export date and time, report period, sub-category, portfolio totals (Total Buy,
			Sold, ST%, MRP Value, GP%, GP Value), and applied filters — <strong>no product images</strong>.
		</Typography>
		<Typography variant="caption" component="div" sx={{ display: "block", mb: 1, lineHeight: 1.5 }}>
			<strong>From page 2:</strong> product pictorial grid only (full page), using your selected tile fields.
		</Typography>
		<Typography variant="caption" component="div" sx={{ display: "block", mb: 1, lineHeight: 1.5 }}>
			<strong>Each product tile:</strong> use <strong>As PDF</strong> to open the field picker and choose which metrics appear
			(image through BLEND). Unchecked fields are omitted only in the PDF; the on-screen grid is unchanged.
		</Typography>
		<Typography variant="caption" component="div" sx={{ lineHeight: 1.5, opacity: 0.85 }}>
			<strong>Not in the PDF:</strong> the &quot;Showing X of Y&quot; / page line above the grid, the pagination control below it,
			or hover-only tooltip breakdowns — only the summary numbers visible on each card are captured.
		</Typography>
	</Box>
);

const pdfExportMenuSecondary =
	"Page 1 = summary & filters only; products from page 2. Pick tile fields (OPTION–BLEND) in the next step.";

/** Ramp buckets by **colour label**, not resolved hex (avoids Off White + White merging to one #fff key). */
function colorContributionMapKey(product) {
	return colorContributionRampBucketKey(product);
}

/**
 * Per-colour % of total for a product metric (only rows with positive values contribute).
 * Row `soldQty` holds the summed units; `hex` is set when the product exposes a hex (or `color` is hex).
 * Optional `getGpValue` — when passed, sum a secondary value per colour and attach `gpSharePct` (% of that total in the slice; sold ramp = GP, T.INW ramp = COGS buy value); bar width stays from the primary metric.
 */
function aggregateColorMetricContributions(products, brandFilter, getMetricValue, getGpValue) {
	if (!products || products.length === 0) return [];

	let list = products;
	if (brandFilter) {
		list = products.filter((p) => (p.brand || "Unknown").toLowerCase() === String(brandFilter).toLowerCase());
	}

	const colorMap = new Map();
	list.forEach((product) => {
		const colorName = String(product.color ?? "Unknown").trim() || "Unknown";
		const hex = getProductColorHex(product);
		const mapKey = colorContributionMapKey(product);
		const qty = Math.round(Number(getMetricValue(product)) || 0);
		const gpAdd = typeof getGpValue === "function" ? getProductGpValue(product) : 0;
		if (qty > 0) {
			if (colorMap.has(mapKey)) {
				const existing = colorMap.get(mapKey);
				existing.soldQty += qty;
				existing.count += 1;
				if (typeof getGpValue === "function") {
					existing.gpValue = (existing.gpValue || 0) + gpAdd;
				}
				if (!existing.hex && hex) existing.hex = hex;
			} else {
				colorMap.set(mapKey, {
					color: colorName,
					...(hex ? { hex } : {}),
					soldQty: qty,
					...(typeof getGpValue === "function" ? { gpValue: gpAdd } : {}),
					count: 1,
				});
			}
		}
	});

	const buckets = Array.from(colorMap.values());
	const totalSold = buckets.reduce((sum, item) => sum + item.soldQty, 0);
	const totalGp =
		typeof getGpValue === "function" ? buckets.reduce((sum, item) => sum + (item.gpValue || 0), 0) : 0;

	return buckets
		.map((item) => ({
			...item,
			percentage: totalSold > 0 ? Math.round((item.soldQty / totalSold) * 100 * 10) / 10 : 0,
			...(typeof getGpValue === "function" && totalGp > 0
				? {
						gpSharePct: Math.round(((item.gpValue || 0) / totalGp) * 100 * 10) / 10,
					}
				: {}),
		}))
		.filter((item) => item.soldQty > 0)
		.sort((a, b) => b.soldQty - a.soldQty);
}

/**
 * Per-colour SOH with WH vs EBO split (soldQty = WH + EBO for ramp width / %).
 */
function aggregateColorSohContributions(products, brandFilter) {
	if (!products || products.length === 0) return [];

	let list = products;
	if (brandFilter) {
		list = products.filter((p) => (p.brand || "Unknown").toLowerCase() === String(brandFilter).toLowerCase());
	}

	const colorMap = new Map();
	list.forEach((product) => {
		const colorName = String(product.color ?? "Unknown").trim() || "Unknown";
		const hex = getProductColorHex(product);
		const mapKey = colorContributionMapKey(product);
		const wh = Math.round(Number(product.total_wh_soh) || 0);
		const ebo = Math.round(Number(product.total_ebo_soh) || 0);
		const total = wh + ebo;
		if (total > 0) {
			if (colorMap.has(mapKey)) {
				const existing = colorMap.get(mapKey);
				existing.soldQty += total;
				existing.whSoh += wh;
				existing.eboSoh += ebo;
				existing.count += 1;
				if (!existing.hex && hex) existing.hex = hex;
			} else {
				colorMap.set(mapKey, {
					color: colorName,
					...(hex ? { hex } : {}),
					soldQty: total,
					whSoh: wh,
					eboSoh: ebo,
					count: 1,
				});
			}
		}
	});

	const grandTotal = Array.from(colorMap.values()).reduce((sum, item) => sum + item.soldQty, 0);

	return Array.from(colorMap.values())
		.map((item) => ({
			...item,
			percentage: grandTotal > 0 ? Math.round((item.soldQty / grandTotal) * 100 * 10) / 10 : 0,
		}))
		.filter((item) => item.soldQty > 0)
		.sort((a, b) => b.soldQty - a.soldQty);
}

/** Per-fashionability % of sold qty (period or overall) for straight divider bar. */
function aggregateFashionabilityMetricContributions(products, brandFilter, getQty) {
	if (!products || products.length === 0) return [];

	let list = products;
	if (brandFilter) {
		list = products.filter((p) => (p.brand || "Unknown").toLowerCase() === String(brandFilter).toLowerCase());
	}

	const fashionMap = new Map();
	list.forEach((product) => {
		const label = String(product.fashionability ?? "Unknown").trim() || "Unknown";
		const mapKey = label.replace(/\s+/g, " ").toLowerCase();
		const qty = Math.round(Number(getQty(product)) || 0);
		if (qty > 0) {
			if (fashionMap.has(mapKey)) {
				fashionMap.get(mapKey).soldQty += qty;
			} else {
				fashionMap.set(mapKey, { fashionability: label, soldQty: qty });
			}
		}
	});

	const buckets = Array.from(fashionMap.values());
	const totalSold = buckets.reduce((sum, item) => sum + item.soldQty, 0);

	return buckets
		.map((item) => ({
			...item,
			percentage: totalSold > 0 ? Math.round((item.soldQty / totalSold) * 100 * 10) / 10 : 0,
		}))
		.filter((item) => item.soldQty > 0)
		.sort((a, b) => b.soldQty - a.soldQty);
}

const aggregateFashionabilitySoldContributions = (products, brandFilter) =>
	aggregateFashionabilityMetricContributions(products, brandFilter, (p) => p.troco_ls?.t_sale);

const aggregateFashionabilityOverallContributions = (products, brandFilter) =>
	aggregateFashionabilityMetricContributions(products, brandFilter, (p) => p.troco_ls?.t_sale_total);

/** Set to `true` to show COLOUR · SOH % ramps (header row + GP slide card) and compute SOH-by-colour data. */
const SOH_COLOR_RAMP_ENABLED = false;

/** Tile metrics the user can include or omit from PDF/CSV export (clone-only for PDF). */
const PICTORIAL_EXPORT_FIELDS = [
	{ id: "summary_header", label: "Summary header (date range, Buy/Sold totals)" },
	{ id: "image", label: "Product image (or placeholder)" },
	{ id: "option", label: "OPTION (PKEY)" },
	{ id: "season", label: "Season" },
	{ id: "category", label: "Category" },
	{ id: "fit_type", label: "Fit (Denim Pants only)" },
	{ id: "mrp", label: "MRP" },
	{ id: "cogs", label: "COGS" },
	{ id: "tinw", label: "T.INW" },
	{ id: "multi_mrp_cogs", label: "Multi (MRP/COGS)" },
	{ id: "sold_in_period", label: "Sold In Period" },
	{ id: "total_sold", label: "Total Sold" },
	{ id: "balance", label: "Balance" },
	{ id: "period_st", label: "Sold Period ST%" },
	{ id: "st", label: "Total ST%" },
	{ id: "gp_pct", label: "GP%" },
	{ id: "online", label: "Online" },
	{ id: "offline", label: "Offline" },
	{ id: "gp_value", label: "GP Value" },
	{ id: "soh", label: "SOH" },
	{ id: "inv_pivotal", label: "Pivotal SOH (S,M,L)" },
	{ id: "inv_non_pivotal", label: "Non-pivotal SOH" },
	{ id: "avg_aging", label: "Aging" },
	{ id: "max_recv_aging", label: "Aging Last Inw" },
	{ id: "first_inw_date", label: "First Inw Date" },
	{ id: "max_inw_date", label: "Last Inw Date" },
	{ id: "avg_doh", label: "Avg DOH" },
	{ id: "period_ros", label: "Period Wgt. ROS" },
	{ id: "stock_cover_period", label: "Stock Cover (period)" },
	{ id: "ros", label: "Wgt. ROS" },
	{ id: "blend", label: "Blend (fabric)" },
	{ id: "palette_type", label: "Palette Type" },
];

/** @deprecated alias — use PICTORIAL_EXPORT_FIELDS */
const PICTORIAL_PDF_TILE_FIELDS = PICTORIAL_EXPORT_FIELDS;

const defaultExportFieldMask = () =>
	Object.fromEntries(PICTORIAL_EXPORT_FIELDS.map(({ id }) => [id, id !== "summary_header"]));

const defaultPdfTileFieldMask = defaultExportFieldMask;

const FILTER_DEPS_DEBOUNCE_MS = 10;

/** Period sell-through: sold in date range ÷ buy (inward) qty. */
const getProductPeriodStPct = (product) => {
	const buy = Number(product?.troco_ls?.t_buy) || 0;
	const sold = Number(product?.troco_ls?.t_sale) || 0;
	if (buy <= 0) return 0;
	return Math.round((sold / buy) * 10000) / 100;
};

const parseProductCogs = (product) => parseFloat(String(product?.COGS ?? "").replace(/,/g, "")) || 0;

/** Per-option GP (₹) — round each option, then sum in breakdowns (consistent with contribution chips). */
const getProductGpValue = (product) => Math.round(parseFloat(product?.lcogs?.gp_value || 0));

const sumGpValueForProducts = (products, { onlyPositive = false } = {}) =>
	(products || []).reduce((sum, product) => {
		const gp = getProductGpValue(product);
		if (onlyPositive && gp <= 0) return sum;
		return sum + gp;
	}, 0);

/** COGS buy value per option (COGS × T.INW) for colour-ramp contribution on the T.INW bar. */
const getProductBuyCogsValue = (product) => {
	const cogs = parseProductCogs(product);
	const inw = Number(product?.troco_ls?.t_inw) || 0;
	return Math.round(cogs * inw);
};

const formatRos = (value) => {
	const n = Number(value);
	if (value == null || value === "" || Number.isNaN(n) || n <= 0) return "—";
	return n.toFixed(2);
};

const PICTORIAL_SUMMARY_BOX_SX = {
	minWidth: 0,
	width: "100%",
	maxWidth: "100%",
	overflow: "hidden",
	border: "1px solid #e0e0e0",
	borderRadius: 1,
	p: { xs: 0.75, sm: 1 },
	pt: { xs: 0.875, sm: 1 },
	bgcolor: "#fff",
	boxSizing: "border-box",
};

const PICTORIAL_SUMMARY_HEADER_SX = {
	mb: 0.75,
	pb: 0.5,
	color: "#000",
	fontWeight: "bold",
	fontSize: "0.75rem",
	textAlign: "center",
	borderBottom: "1px solid #eee",
};

const PICTORIAL_SUMMARY_ROW_SX = {
	mb: 0.5,
	color: "#000",
	display: "grid",
	gridTemplateColumns: "minmax(0, 1fr) auto",
	columnGap: 0.75,
	alignItems: "baseline",
	fontSize: { xs: "0.625rem", sm: "0.6875rem" },
	lineHeight: 1.25,
	"& > *:first-child": {
		minWidth: 0,
		fontWeight: "bold",
	},
	"& > *:last-child": {
		textAlign: "right",
		fontVariantNumeric: "tabular-nums",
		minWidth: 0,
	},
};

const PICTORIAL_SUMMARY_VALUE_SX = {
	textAlign: "right",
	fontVariantNumeric: "tabular-nums",
	minWidth: 0,
};

const PICTORIAL_SUMMARY_SUBVALUE_SX = {
	color: "#666",
	fontWeight: 400,
	fontSize: "0.58rem",
	display: "block",
	lineHeight: 1.1,
	textAlign: "right",
};

/** Sale-channel keys for period vs overall sold-pcs split in the summary header. */
const PICTORIAL_CHANNEL_SOLD_DEF = [
	{ label: "EBO", periodKey: "ebo_sold_qty", overallKey: "ebo_sold_qty_total" },
	{ label: "LFS", periodKey: "lfs_sold_qty", overallKey: "lfs_sold_qty_total" },
	{ label: "MBO", periodKey: "mbo_sold_qty", overallKey: "mbo_sold_qty_total" },
	{ label: "D2C", periodKey: "d2c_sold_qty", overallKey: "d2c_sold_qty_total" },
	{ label: "MP", periodKey: "marketplace_sold_qty", overallKey: "marketplace_sold_qty_total" },
	{ label: "Other", periodKey: "other_sold_qty", overallKey: "other_sold_qty_total" },
];

const PICTORIAL_CHANNEL_SOLD_GRID_SX = {
	...PICTORIAL_SUMMARY_ROW_SX,
	gridTemplateColumns: "minmax(0, auto) minmax(0, 1fr) minmax(0, 1fr)",
	columnGap: 0.75,
	alignItems: "baseline",
	width: "100%",
};

const PICTORIAL_CHANNEL_SOLD_VALUE_SX = {
	...PICTORIAL_SUMMARY_VALUE_SX,
	whiteSpace: "nowrap",
};

const PICTORIAL_SUMMARY_STRIP_GRID_SX = {
	display: "grid",
	width: "100%",
	minWidth: 0,
	gap: 1.25,
	gridTemplateColumns: {
		xs: "1fr",
		sm: "repeat(2, minmax(0, 1fr))",
		md: "repeat(4, minmax(0, 1fr))",
	},
};

const PICTORIAL_SUMMARY_BAL_SX = PICTORIAL_SUMMARY_SUBVALUE_SX;

const channelSoldContributionPct = (part, total) => {
	if (!total || total <= 0) return "0.0";
	return (Math.round((part / total) * 1000) / 10).toFixed(1);
};

const EXPORT_CSV_FIELD_MAP = {
	option: { header: "Option", getValue: (p) => p.PKEY || "N/A" },
	brand: { header: "Brand", getValue: (p) => p.brand || "N/A" },
	subBrand: { header: "Sub Brand", getValue: (p) => p.subBrand || "N/A" },
	department: { header: "Department", getValue: (p) => p.category || "N/A" },
	subCategory: { header: "Sub Category", getValue: (p) => p.sub_category || "N/A" },
	style: { header: "Style", getValue: (p) => p.style || "N/A" },
	color: { header: "Color", getValue: (p) => p.color || "N/A" },
	season: { header: "Season", getValue: (p) => p.season || "N/A" },
	fit_type: { header: "Fit Type", getValue: (p) => p.fitType || "N/A" },
	mrp: { header: "MRP", getValue: (p) => p.troco_ls?.mrp || "N/A" },
	cogs: { header: "COGS", getValue: (p) => p.COGS || "N/A" },
	tinw: { header: "T.INW", getValue: (p) => p.troco_ls?.t_inw || "0" },
	multi_mrp_cogs: {
		header: "Multi (MRP/COGS)",
		getValue: (p) => {
			const mrp = parseFloat(String(p.troco_ls?.mrp ?? "").replace(/,/g, "")) || 0;
			const cogs = parseFloat(String(p.COGS ?? "").replace(/,/g, "")) || 0;
			if (!cogs) return "—";
			return (mrp / cogs).toFixed(2);
		},
	},
	sold_in_period: { header: "Sold In Period", getValue: (p) => p.troco_ls?.t_sale ?? "0" },
	total_sold: { header: "Total Sold", getValue: (p) => p.troco_ls?.t_sale_total ?? "0" },
	balance: { header: "Balance", getValue: (p) => p.troco_ls?.balance ?? "0" },
	period_st: {
		header: "Sold Period ST%",
		getValue: (p) => `${getProductPeriodStPct(p).toFixed(2)}%`,
	},
	st: {
		header: "Total ST%",
		getValue: (p) => `${(p.troco_ls?.st ?? 0).toFixed(2)}%`,
	},
	gp_pct: {
		header: "GP%",
		getValue: (p) => p.lcogs?.gp_percentage || "0%",
	},
	gp_value: { header: "GP Value", getValue: (p) => p.lcogs?.gp_value || "0" },
	soh: {
		header: "SOH",
		getValue: (p) => (p.total_wh_soh || 0) + (p.total_ebo_soh || 0),
	},
	avg_aging: {
		header: "Aging (days)",
		getValue: (p) => {
			if (p.avg_aging != null && p.avg_aging !== "" && !Number.isNaN(Number(p.avg_aging))) {
				return Math.round(Number(p.avg_aging));
			}
			const vals = [p.ebo_aging, p.lfs_aging, p.mbo_aging, p.ecom_aging].filter((v) => Number(v) > 0);
			if (vals.length === 0) return "0";
			return Math.round(vals.reduce((s, v) => s + Number(v), 0) / vals.length);
		},
	},
	max_recv_aging: {
		header: "Aging Last Inw (days)",
		getValue: (p) => {
			if (p.avg_max_aging != null && p.avg_max_aging !== "" && !Number.isNaN(Number(p.avg_max_aging))) {
				return Math.round(Number(p.avg_max_aging));
			}
			const vals = [p.ebo_max_aging, p.lfs_max_aging, p.mbo_max_aging, p.ecom_max_aging].filter((v) => Number(v) > 0);
			if (vals.length === 0) return "0";
			return Math.round(vals.reduce((s, v) => s + Number(v), 0) / vals.length);
		},
	},
	first_inw_date: {
		header: "First Inw Date",
		getValue: (p) => p.first_inw_date || "—",
	},
	max_inw_date: {
		header: "Last Inw Date",
		getValue: (p) => p.max_inw_date || "—",
	},
	period_ros: { header: "Period Wgt. ROS", getValue: (p) => formatRos(p.period_ros) },
	stock_cover_period: { header: "Stock Cover (days)", getValue: (p) => p.stock_cover_period ?? "0" },
	ros: { header: "Wgt. ROS", getValue: (p) => formatRos(p.rate_of_sale) },
	blend: { header: "Blend", getValue: (p) => p.blend || p.fabricBlends || "N/A" },
	palette_type: {
		header: "Palette Type",
		getValue: (p) => p.paletteType || p.palette || "N/A",
	},
	online: {
		header: "Online Cont%",
		getValue: (p) => `${p.lcogs?.oln_cont || "0%"} (${p.online_sold_qty || 0})`,
	},
	offline: {
		header: "Offline Cont%",
		getValue: (p) => `${p.lcogs?.offln_cont || "0%"} (${p.offline_sold_qty || 0})`,
	},
};

const TILE_EXPORT_FIELD_IDS = PICTORIAL_EXPORT_FIELDS.filter(({ id }) => id !== "summary_header").map(
	({ id }) => id
);

const hasTileExportFieldsSelected = (mask) => TILE_EXPORT_FIELD_IDS.some((id) => mask[id]);

/** Hide unchecked `[data-pdf-field]` nodes in the cloned DOM only. */
const applyPdfFieldMaskToClone = (clonedDoc, mask) => {
	if (!clonedDoc || !mask) return;
	clonedDoc.querySelectorAll("[data-pdf-field]").forEach((el) => {
		const id = el.getAttribute("data-pdf-field");
		if (!id) return;
		if (mask[id] === false) {
			el.style.display = "none";
		}
	});
};

/** Match ProductCard metrics in PDF clone: 5× minmax(0,1fr), small type, label ellipsis, value wrap, smaller GP */
const PDF_METRIC_LABEL_FONT_PX = 8;
const PDF_METRIC_VALUE_FONT_PX = 8;
const PDF_METRIC_GP_VALUE_FONT_PX = 7;
const PDF_METRIC_COL_GAP_PX = 4;
const PDF_METRIC_ROW_GAP_PX = 4;
const PDF_METRIC_CELL_PADDING_RIGHT_PX = 3;

/**
 * After field mask: reflow visible metric cells into an even grid (1–6 columns)
 * so PDF tiles match the live ProductCard (no horizontal scroll, compact font, aligned columns).
 */
const reflowPdfMetricsGridsInClone = (clonedDoc) => {
	if (!clonedDoc) return;
	const metricFieldOrder = PICTORIAL_PDF_TILE_FIELDS.map(({ id }) => id).filter((id) => id !== "image" && id !== "option");

	clonedDoc.querySelectorAll("[data-pdf-metrics-grid]").forEach((grid) => {
		const cellsInOrder = metricFieldOrder
			.map((id) => grid.querySelector(`[data-pdf-field="${id}"]`))
			.filter(Boolean);
		const visible = cellsInOrder.filter((el) => el.style.display !== "none");

		while (grid.firstChild) {
			grid.removeChild(grid.firstChild);
		}

		if (visible.length === 0) {
			grid.style.display = "none";
			return;
		}

		const cols = Math.min(6, visible.length);
		grid.style.display = "grid";
		grid.style.gridTemplateColumns = `repeat(${cols}, minmax(0, 1fr))`;
		grid.style.maxWidth = "100%";
		grid.style.width = "100%";
		grid.style.boxSizing = "border-box";
		grid.style.columnGap = `${PDF_METRIC_COL_GAP_PX}px`;
		grid.style.rowGap = `${PDF_METRIC_ROW_GAP_PX}px`;
		grid.style.alignItems = "start";
		grid.style.justifyItems = "stretch";
		grid.style.textAlign = "left";
		grid.style.lineHeight = "1.2";

		visible.forEach((el) => {
			el.style.display = "";
			el.style.gridRow = "auto";
			const fieldId = el.getAttribute("data-pdf-field");
			if ((fieldId === "blend" || fieldId === "palette_type") && cols > 1) {
				el.style.gridColumn = "span 1";
			} else {
				el.style.removeProperty("grid-column");
			}
			el.style.removeProperty("grid-row");
			el.style.width = "100%";
			el.style.minWidth = "0";
			el.style.maxWidth = "100%";
			el.style.textAlign = "left";
			el.style.boxSizing = "border-box";
			el.style.paddingRight = `${PDF_METRIC_CELL_PADDING_RIGHT_PX}px`;
			el.style.overflowWrap = "anywhere";
			el.style.wordBreak = "break-word";

			/* Tooltip cells nest Typographies; direct children are not always the two lines */
			const typos = el.querySelectorAll(".MuiTypography-root");
			if (typos.length >= 1) {
				const labelEl = typos[0];
				labelEl.style.fontSize = `${PDF_METRIC_LABEL_FONT_PX}px`;
				labelEl.style.fontWeight = "700";
				labelEl.style.lineHeight = "1.2";
				labelEl.style.whiteSpace = "normal";
				labelEl.style.overflowWrap = "anywhere";
				labelEl.style.wordBreak = "break-word";
				labelEl.style.maxWidth = "100%";
			}
			if (typos.length >= 2) {
				const valueEl = typos[1];
				const valuePx =
					fieldId === "gp_value" || fieldId === "multi_mrp_cogs"
						? PDF_METRIC_GP_VALUE_FONT_PX
						: PDF_METRIC_VALUE_FONT_PX;
				valueEl.style.fontSize = `${valuePx}px`;
				valueEl.style.fontWeight = "400";
				valueEl.style.lineHeight = "1.2";
				valueEl.style.whiteSpace = "normal";
				valueEl.style.maxWidth = "100%";
				valueEl.style.minWidth = "0";
				valueEl.style.overflowWrap = "anywhere";
				valueEl.style.wordBreak = "break-word";
				if (fieldId === "gp_value" || fieldId === "multi_mrp_cogs") {
					valueEl.style.fontVariantNumeric = "tabular-nums";
				}
			}

			grid.appendChild(el);
		});
	});
};

/**
 * Match clone image stage size to the live DOM, then render packshots with contain + flex center in the clone only.
 * Avoids html2canvas stretching images horizontally; may add small letterboxing inside the tile.
 */
const syncPdfImageStagesToLiveLayout = (liveRoot, clonedDoc) => {
	if (!liveRoot || !clonedDoc) return;
	const origStages = liveRoot.querySelectorAll("[data-pdf-image-stage]");
	const cloneStages = clonedDoc.querySelectorAll("[data-pdf-image-stage]");
	for (let i = 0; i < origStages.length; i++) {
		const o = origStages[i];
		const c = cloneStages[i];
		if (!o || !c) continue;
		const r = o.getBoundingClientRect();
		const h = Math.max(1, Math.round(r.height));
		const w = Math.max(1, Math.round(r.width));
		c.style.boxSizing = "border-box";
		c.style.height = `${h}px`;
		c.style.width = `${w}px`;
		c.style.minHeight = `${h}px`;
		c.style.maxHeight = `${h}px`;
		c.style.minWidth = `${w}px`;
		c.style.maxWidth = `${w}px`;
		c.style.overflow = "hidden";
		c.style.display = "flex";
		c.style.alignItems = "center";
		c.style.justifyContent = "center";
		const oImg = o.querySelector("img");
		const cImg = c.querySelector("img");
		if (cImg && oImg) {
			cImg.style.flexShrink = "0";
			cImg.style.width = "auto";
			cImg.style.height = "auto";
			cImg.style.maxWidth = "100%";
			cImg.style.maxHeight = "100%";
			cImg.style.objectFit = "contain";
			cImg.style.objectPosition = "center center";
		}
	}
};

const preparePdfDomClone = (liveRoot, clonedDoc, fieldMask) => {
	syncPdfImageStagesToLiveLayout(liveRoot, clonedDoc);
	applyPdfFieldMaskToClone(clonedDoc, fieldMask);
	reflowPdfMetricsGridsInClone(clonedDoc);
};

const PictorialST = () => {
	const [products, setProducts] = useState([]);
	const [error, setError] = useState("");
	const [loading, setLoading] = useState(false);
	/** Overlay copy: BigQuery round-trip vs client transform/pagination */
	const [dataLoadPhase, setDataLoadPhase] = useState(null);
	const [queryProgress, setQueryProgress] = useState(0);
	const [pdfGenerating, setPdfGenerating] = useState(false);
	const [pdfProgress, setPdfProgress] = useState(0);
	const [filterOptionsLoading, setFilterOptionsLoading] = useState(true);
	const [hoveredSort, setHoveredSort] = useState(null);
	const [currentPageDownloadAnchor, setCurrentPageDownloadAnchor] = useState(null);
	const [allPagesDownloadAnchor, setAllPagesDownloadAnchor] = useState(null);
	const [currentPageDownloadOpen, setCurrentPageDownloadOpen] = useState(false);
	const [allPagesDownloadOpen, setAllPagesDownloadOpen] = useState(false);
	const [uploadModalOpen, setUploadModalOpen] = useState(false);
	const [pdfFieldPickerOpen, setPdfFieldPickerOpen] = useState(false);
	const [pdfFieldPickerScope, setPdfFieldPickerScope] = useState("current");
	const [exportPickerFormat, setExportPickerFormat] = useState("pdf");
	const [pdfFieldSelection, setPdfFieldSelection] = useState(() => defaultExportFieldMask());
	const pdfExportFieldMaskRef = useRef(defaultPdfTileFieldMask());
	const [dateRange, setDateRange] = useState({
		startDate: new Date(),
		endDate: new Date(),
	});
	const [pendingDateRange, setPendingDateRange] = useState({
		startDate: new Date(),
		endDate: new Date(),
	});
	const [totals, setTotals] = useState({
		totalBuy: 0,
		totalSold: 0,
		subCategory: "",
		avgASP: 0,
		avgMRP: 0,
		avgCOGS: 0,
		gpValue: 0,
		gpPercentage: 0,
		mrpValue: 0,
		soldMRPValue: 0,
		percentageSold: 0,
		salesContribution: 0,
		gpContribution: 0,
		buyCogsValue: 0,
		buyMulti: 0,
		soldCogsValue: 0,
		soldMulti: 0,
		totalSoldLifetime: 0,
		buyAvgCOGS: 0,
		avgMRPSold: 0,
	});

	// State for company totals (without filters)
	const [companyTotals, setCompanyTotals] = useState({
		totalBuy: 0,
		totalSold: 0,
		totalSoldOverall: 0,
		balance: 0,
		periodStPct: 0,
		totalStPct: 0,
		gpValue: 0,
		mrpBuyValue: 0,
		mrpSoldValue: 0,
	});
	const [pagination, setPagination] = useState({
		page: 1,
		per_page: 15,
		total_pages: 1,
		total: 0,
		isPageChange: false,
	});
	const [filters, setFilters] = useState({
		department: "",
		subCategory: "",
		brand: "",
		subBrand: "",
		style: "",
		color: "",
		sleeve: "",
		division: "",
		season: "",
		fashionability: "",
		occasion: "",
		palette: "",
		fitType: "",
		fabricBlend: "",
		nod: "",
		channel: "",
		store: "",
		city: "",
		state: "",
		region: "",
		tier: "",
		aging: "",
		st_percentage: "",
		start_date: "",
		end_date: "",
	});

	const [pendingFilters, setPendingFilters] = useState({
		department: "",
		subCategory: "",
		brand: "",
		subBrand: "",
		style: "",
		color: "",
		sleeve: "",
		division: "",
		season: "",
		fashionability: "",
		occasion: "",
		palette: "",
		fitType: "",
		fabricBlend: "",
		nod: "",
		channel: "",
		store: "",
		city: "",
		state: "",
		region: "",
		tier: "",
		aging: "",
		st_percentage: "",
		start_date: "",
		end_date: "",
	});

	const pdfRef = useRef(null);
	const [currentDateTime, setCurrentDateTime] = useState(new Date());
	const [filterOptions, setFilterOptions] = useState({
		departments: [],
		subCategories: [],
		brands: [],
		subBrands: [],
		styles: [],
		colors: [],
		sleeves: [],
		divisions: [],
		seasons: [],
		fashionability: [],
		occasions: [],
		palettes: [],
		fitTypes: [],
		fabricBlends: [],
		nods: [],
		channels: [],
		stores: [],
		cities: [], // Add test cities
		states: [],
		regions: [],
		tiers: [],
		aging: [],
		st_percentage: [],
	});

	// Add state for master filter options
	const [masterFilterOptions, setMasterFilterOptions] = useState({});

	const [sortConfig, setSortConfig] = useState({
		field: "period_st",
		direction: "desc",
	});

	// Add new state for search terms and selected items
	const [searchTerms, setSearchTerms] = useState({
		brand: "",
		subBrand: "",
		department: "",
		subCategory: "",
		style: "",
		color: "",
		sleeve: "",
		division: "",
		season: "",
		fashionability: "",
		occasion: "",
		palette: "",
		fitType: "",
		fabricBlend: "",
		nod: "",
		channel: "",
		store: "",
		city: "",
		state: "",
		region: "",
		tier: "",
	});

	const [selectedItems, setSelectedItems] = useState({
		brands: new Set(),
		subBrands: new Set(),
		departments: new Set(),
		subCategories: new Set(),
		styles: new Set(),
		colors: new Set(),
		sleeves: new Set(),
		divisions: new Set(),
		seasons: new Set(),
		fashionability: new Set(),
		occasions: new Set(),
		palettes: new Set(),
		fitTypes: new Set(),
		fabricBlends: new Set(),
		nods: new Set(),
		channels: new Set(),
		stores: new Set(),
		cities: new Set(),
		states: new Set(),
		regions: new Set(),
		tiers: new Set(),
	});

	// Add new state for all products
	const [allProducts, setAllProducts] = useState([]);
	/** Backend-resolved channel for ROS / Avg Aging (e.g. store's channel when Store filter is on). */
	const [metricsChannel, setMetricsChannel] = useState(null);
	// "WH" | "EBO" | null — client-side quick filters (only one group active at a time)
	const [brokenFilter, setBrokenFilter] = useState(null);
	const [availableFilter, setAvailableFilter] = useState(null);
	const [fullSizeSetFilter, setFullSizeSetFilter] = useState(null);

	/** Colour ramp segment drilldown: grid shows only products in this bucket (toggle same segment to clear). */
	const [rampColorDrilldown, setRampColorDrilldown] = useState(null);
	/** Brief overlay on the product grid when a ramp colour is selected (UX feedback while the slice updates). */
	const [rampGridSelectionOverlay, setRampGridSelectionOverlay] = useState(false);
	const rampDrilldownRef = useRef(null);
	const rampGridOverlayTimeoutRef = useRef(null);
	rampDrilldownRef.current = rampColorDrilldown;

	// Add product cache for category filtering
	const [productCache, setProductCache] = useState(new Map());

	// Helper function to check if any filters are active
	const hasActiveFilters = () => {
		return Object.values(filters).some((value) => value && value.trim() !== "");
	};

	// Add state for GP Contribution tooltip
	// GP Contribution card state
	const [gpContributionCard, setGpContributionCard] = useState({
		open: false,
		brandContributions: [],
		categoryContributions: [],
		colorContributions: [],
		colorContributionsTinw: [],
		colorContributionsSoh: [],
		selectedBrand: null,
		viewMode: "brands", // 'brands' | 'categories' | 'colors'
		colorViewFrom: null, // 'brands' | 'categories' — used when backing out of colors view
		transitioning: false,
	});
	const [gpContributionAnchorEl, setGpContributionAnchorEl] = useState(null);

	const closeGpContributionCard = useCallback(() => {
		setGpContributionCard((prev) => ({ ...prev, open: false }));
		setGpContributionAnchorEl(null);
	}, []);

	// Add new state for dashboard metrics

	// Add state to track open dropdowns
	const [openDropdowns, setOpenDropdowns] = useState(new Set());
	const prevOpenDropdownsSize = useRef(0);
	const filtersWhenDropdownOpened = useRef(null);

	// Add state for expand/collapse filters section
	const [filtersExpanded, setFiltersExpanded] = useState(true);

	// Add a ref to store the cancel token
	const cancelTokenRef = useRef(null);

	// Add a ref for tracking the latest request
	const currentRequestIdRef = useRef(0);

	// Add a ref to track if we're in the middle of applying filters
	const isApplyingFiltersRef = useRef(false);

	// Add ref to track if filter options have been loaded
	const filterOptionsLoadedRef = useRef(false);

	// Add ref to track current filters to avoid dependency issues
	const filtersRef = useRef(filters);
	const filterDepsAbortRef = useRef(null);
	const filterDepsSeqRef = useRef(0);
	const filterDepsDebounceRef = useRef(null);

	// Update filtersRef whenever filters change
	useEffect(() => {
		filtersRef.current = filters;
	}, [filters]);

	// Function to calculate brand-wise GP contributions
	const calculateBrandContributions = useCallback((products) => {
		if (!products || products.length === 0) return [];

		const brandMap = new Map();

		products.forEach((product) => {
			const brand = product.brand || "Unknown";
			const gpValue = getProductGpValue(product);

			if (brandMap.has(brand)) {
				const existing = brandMap.get(brand);
				existing.gpValue += gpValue;
				existing.count += 1;
			} else {
				brandMap.set(brand, {
					brand,
					gpValue,
					count: 1,
				});
			}
		});

		const totalGPValue = Array.from(brandMap.values()).reduce((sum, item) => sum + item.gpValue, 0);

		return Array.from(brandMap.values())
			.map((item) => ({
				...item,
				percentage: totalGPValue > 0 ? Math.round((item.gpValue / totalGPValue) * 100 * 10) / 10 : 0,
			}))
			.filter((item) => item.gpValue !== 0)
			.sort((a, b) => b.gpValue - a.gpValue);
	}, []);

	// Function to calculate category-wise GP contributions within a selected brand
	const calculateCategoryContributions = useCallback((products, selectedBrand) => {
		if (!products || products.length === 0 || !selectedBrand) {
			return [];
		}

		// Filter products for the selected brand (case-insensitive)
		const brandProducts = products.filter((product) => (product.brand || "Unknown").toLowerCase() === selectedBrand.toLowerCase());

		if (brandProducts.length === 0) {
			return [];
		}

		const categoryMap = new Map();

		brandProducts.forEach((product) => {
			const category = product.category || "Unknown";
			const gpValue = getProductGpValue(product);

			if (categoryMap.has(category)) {
				const existing = categoryMap.get(category);
				existing.gpValue += gpValue;
				existing.count += 1;
			} else {
				categoryMap.set(category, {
					category,
					gpValue,
					count: 1,
				});
			}
		});

		// Calculate total GP value for this brand (should be 100% of brand's contribution)
		const brandTotalGPValue = Array.from(categoryMap.values()).reduce((sum, item) => sum + item.gpValue, 0);

		return Array.from(categoryMap.values())
			.map((item) => ({
				...item,
				percentage: brandTotalGPValue > 0 ? Math.round((item.gpValue / brandTotalGPValue) * 100 * 10) / 10 : 0,
			}))
			.filter((item) => item.gpValue !== 0)
			.sort((a, b) => b.gpValue - a.gpValue);
	}, []);

	const calculateColorContributions = useCallback(
		(products, brandFilter = null) =>
			aggregateColorMetricContributions(
				products,
				brandFilter,
				(p) => p.troco_ls?.t_sale,
				(p) => p.lcogs?.gp_value
			),
		[]
	);

	const calculateColorContributionsOverall = useCallback(
		(products, brandFilter = null) =>
			aggregateColorMetricContributions(
				products,
				brandFilter,
				(p) => p.troco_ls?.t_sale_total,
				(p) => p.lcogs?.gp_value
			),
		[]
	);

	const calculateColorContributionsTinw = useCallback(
		(products, brandFilter = null) =>
			aggregateColorMetricContributions(
				products,
				brandFilter,
				(p) => p.troco_ls?.t_inw,
				getProductBuyCogsValue
			),
		[]
	);

	const calculateColorContributionsSoh = useCallback(
		(products, brandFilter = null) => aggregateColorSohContributions(products, brandFilter),
		[]
	);

	const calculateFashionabilityContributions = useCallback(
		(products, brandFilter = null) => aggregateFashionabilitySoldContributions(products, brandFilter),
		[]
	);

	const calculateFashionabilityContributionsOverall = useCallback(
		(products, brandFilter = null) => aggregateFashionabilityOverallContributions(products, brandFilter),
		[]
	);

	const colorContributionRowsLoaded = useMemo(
		() => calculateColorContributions(allProducts, null),
		[allProducts, calculateColorContributions]
	);

	const colorContributionRowsOverallLoaded = useMemo(
		() => calculateColorContributionsOverall(allProducts, null),
		[allProducts, calculateColorContributionsOverall]
	);

	const colorContributionRowsTinwLoaded = useMemo(
		() => calculateColorContributionsTinw(allProducts, null),
		[allProducts, calculateColorContributionsTinw]
	);

	const colorContributionRowsSohLoaded = useMemo(
		() => (SOH_COLOR_RAMP_ENABLED ? calculateColorContributionsSoh(allProducts, null) : []),
		[allProducts, calculateColorContributionsSoh]
	);

	const fashionabilityContributionRowsLoaded = useMemo(
		() => calculateFashionabilityContributions(allProducts, null),
		[allProducts, calculateFashionabilityContributions]
	);

	const fashionabilityContributionRowsOverallLoaded = useMemo(
		() => calculateFashionabilityContributionsOverall(allProducts, null),
		[allProducts, calculateFashionabilityContributionsOverall]
	);

	const allProductsForView = useMemo(() => {
		if (!rampColorDrilldown?.key) return allProducts;
		if (
			rampColorDrilldown.dimension === "fashionability" ||
			rampColorDrilldown.dimension === "fashionability_overall"
		) {
			return allProducts.filter((p) => fashionabilityContributionMapKey(p) === rampColorDrilldown.key);
		}
		return allProducts.filter((p) => colorContributionMapKey(p) === rampColorDrilldown.key);
	}, [allProducts, rampColorDrilldown]);

	const getPivotalSizeRows = (p) =>
		Array.isArray(p.size_soh_breakdown)
			? p.size_soh_breakdown.filter((r) => r.pivotal || r.is_pivotal)
			: [];

	const getAllSizeRows = (p) =>
		Array.isArray(p.size_soh_breakdown) ? p.size_soh_breakdown : [];

	/** Returns true when any pivotal size has 0 WH SOH */
	const isProductWhBroken = (p) => {
		const pivRows = getPivotalSizeRows(p);
		return pivRows.length > 0 && pivRows.some((r) => (r.wh_soh || 0) === 0);
	};

	/** Returns true when any pivotal size has 0 EBO SOH */
	const isProductEboBroken = (p) => {
		const pivRows = getPivotalSizeRows(p);
		return pivRows.length > 0 && pivRows.some((r) => (r.ebo_soh || 0) === 0);
	};

	/** All pivotal sizes have WH SOH > 0 */
	const isProductWhPivotalAvailable = (p) => {
		const pivRows = getPivotalSizeRows(p);
		if (pivRows.length === 0) return (p.pivotal_wh_soh || 0) > 0;
		return pivRows.every((r) => (r.wh_soh || 0) > 0);
	};

	/** All pivotal sizes have EBO SOH > 0 */
	const isProductEboPivotalAvailable = (p) => {
		const pivRows = getPivotalSizeRows(p);
		if (pivRows.length === 0) return (p.pivotal_ebo_soh || 0) > 0;
		return pivRows.every((r) => (r.ebo_soh || 0) > 0);
	};

	/** Every size in the curve has WH SOH > 0 */
	const isProductWhFullSizeSet = (p) => {
		const rows = getAllSizeRows(p);
		return rows.length > 0 && rows.every((r) => (r.wh_soh || 0) > 0);
	};

	/** Every size in the curve has EBO SOH > 0 */
	const isProductEboFullSizeSet = (p) => {
		const rows = getAllSizeRows(p);
		return rows.length > 0 && rows.every((r) => (r.ebo_soh || 0) > 0);
	};

	const clearOtherQuickFilters = useCallback((keep) => {
		if (keep !== "broken") setBrokenFilter(null);
		if (keep !== "available") setAvailableFilter(null);
		if (keep !== "fullSizeSet") setFullSizeSetFilter(null);
	}, []);

	const handleBrokenFilterChange = useCallback(
		(_, val) => {
			setBrokenFilter(val);
			if (val) clearOtherQuickFilters("broken");
		},
		[clearOtherQuickFilters]
	);

	const handleAvailableFilterChange = useCallback(
		(_, val) => {
			setAvailableFilter(val);
			if (val) clearOtherQuickFilters("available");
		},
		[clearOtherQuickFilters]
	);

	const handleFullSizeSetFilterChange = useCallback(
		(_, val) => {
			setFullSizeSetFilter(val);
			if (val) clearOtherQuickFilters("fullSizeSet");
		},
		[clearOtherQuickFilters]
	);

	const activeQuickFilter = brokenFilter || availableFilter || fullSizeSetFilter;

	const quickFilterExportLabel = useMemo(() => {
		if (brokenFilter === "WH") return "Pivotal Broken — WH Broken";
		if (brokenFilter === "EBO") return "Pivotal Broken — EBO Broken";
		if (availableFilter === "WH") return "Pivotal Available — WH Available";
		if (availableFilter === "EBO") return "Pivotal Available — EBO Available";
		if (fullSizeSetFilter === "WH") return "Full Size Set — WH Full Set";
		if (fullSizeSetFilter === "EBO") return "Full Size Set — EBO Full Set";
		return null;
	}, [brokenFilter, availableFilter, fullSizeSetFilter]);

	// Counts across the FULL result set (not just current page)
	const whBrokenCount  = useMemo(() => allProductsForView.filter(isProductWhBroken).length,  [allProductsForView]);
	const eboBrokenCount = useMemo(() => allProductsForView.filter(isProductEboBroken).length, [allProductsForView]);
	const whAvailableCount  = useMemo(() => allProductsForView.filter(isProductWhPivotalAvailable).length,  [allProductsForView]);
	const eboAvailableCount = useMemo(() => allProductsForView.filter(isProductEboPivotalAvailable).length, [allProductsForView]);
	const whFullSizeSetCount  = useMemo(() => allProductsForView.filter(isProductWhFullSizeSet).length,  [allProductsForView]);
	const eboFullSizeSetCount = useMemo(() => allProductsForView.filter(isProductEboFullSizeSet).length, [allProductsForView]);

	// Apply quick filter BEFORE pagination so it works across all pages
	const paginationSourceList = useMemo(() => {
		if (brokenFilter === "WH") return allProductsForView.filter(isProductWhBroken);
		if (brokenFilter === "EBO") return allProductsForView.filter(isProductEboBroken);
		if (availableFilter === "WH") return allProductsForView.filter(isProductWhPivotalAvailable);
		if (availableFilter === "EBO") return allProductsForView.filter(isProductEboPivotalAvailable);
		if (fullSizeSetFilter === "WH") return allProductsForView.filter(isProductWhFullSizeSet);
		if (fullSizeSetFilter === "EBO") return allProductsForView.filter(isProductEboFullSizeSet);
		return allProductsForView;
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [allProductsForView, brokenFilter, availableFilter, fullSizeSetFilter]);

	/** Multi-page export uses filtered list when a stock quick filter is selected */
	const productsForAllPagesExport = activeQuickFilter ? paginationSourceList : allProducts;

	const clearRampGridOverlayTimeout = useCallback(() => {
		if (rampGridOverlayTimeoutRef.current != null) {
			clearTimeout(rampGridOverlayTimeoutRef.current);
			rampGridOverlayTimeoutRef.current = null;
		}
	}, []);

	const handleRampSegmentClick = useCallback(
		(row, dimension = "color") => {
			const isFashionabilityRamp =
				dimension === "fashionability" || dimension === "fashionability_overall";
			const key = isFashionabilityRamp
				? fashionabilityContributionRowKey(row)
				: colorContributionRowMapKey(row);
			const label = isFashionabilityRamp
				? String(row?.fashionability ?? "").trim() || "Fashionability"
				: String(row?.color ?? "").trim() || "Colour";
			const prev = rampDrilldownRef.current;
			const prevDimension = prev?.dimension ?? "color";
			const clearing = prev?.key === key && prevDimension === dimension;
			clearRampGridOverlayTimeout();
			setRampColorDrilldown(clearing ? null : { key, label, dimension });
			if (clearing) {
				setRampGridSelectionOverlay(false);
			} else {
				setRampGridSelectionOverlay(true);
				rampGridOverlayTimeoutRef.current = setTimeout(() => {
					rampGridOverlayTimeoutRef.current = null;
					setRampGridSelectionOverlay(false);
				}, 320);
			}
		},
		[clearRampGridOverlayTimeout]
	);

	const rampColorSelectedKey =
		rampColorDrilldown?.dimension === "color" || !rampColorDrilldown?.dimension
			? rampColorDrilldown?.key ?? null
			: null;
	const rampColorOverallSelectedKey =
		rampColorDrilldown?.dimension === "color_overall" ? rampColorDrilldown?.key ?? null : null;
	const rampFashionabilitySelectedKey =
		rampColorDrilldown?.dimension === "fashionability" ? rampColorDrilldown?.key ?? null : null;
	const rampFashionabilityOverallSelectedKey =
		rampColorDrilldown?.dimension === "fashionability_overall" ? rampColorDrilldown?.key ?? null : null;

	const prevRampFilterKeyRef = useRef(null);
	const prevQuickFilterRef = useRef(null);
	// Holds the PKEY we need to scroll-to after a page change renders
	const pendingScrollPkeyRef = useRef(null);
	/** When jumping from WH/EBO broken view to a non-broken card: target page in full list after filter clears */
	const pendingScrollJumpRef = useRef(null);

	useEffect(() => {
		if (rampColorDrilldown) return;
		clearRampGridOverlayTimeout();
		setRampGridSelectionOverlay(false);
	}, [rampColorDrilldown, clearRampGridOverlayTimeout]);

	useEffect(() => {
		return () => clearRampGridOverlayTimeout();
	}, [clearRampGridOverlayTimeout]);

	useEffect(() => {
		const list = paginationSourceList;
		const per = pagination.per_page;
		const tp = Math.max(1, Math.ceil(list.length / per));
		const currentKey = rampColorDrilldown
			? `${rampColorDrilldown.dimension ?? "color"}:${rampColorDrilldown.key}`
			: null;
		const rampKeyChanged = prevRampFilterKeyRef.current !== currentKey;
		prevRampFilterKeyRef.current = currentKey;
		const quickFilterKey = `${brokenFilter || ""}|${availableFilter || ""}|${fullSizeSetFilter || ""}`;
		const quickFilterChanged = prevQuickFilterRef.current !== quickFilterKey;
		prevQuickFilterRef.current = quickFilterKey;
		const nextPage = (rampKeyChanged || quickFilterChanged) ? 1 : Math.min(pagination.page, tp);
		let safePage = Math.min(nextPage, tp);
		const jump = pendingScrollJumpRef.current;
		if (jump?.pkey != null && jump.page != null && list.some((p) => p.PKEY === jump.pkey)) {
			safePage = Math.min(Math.max(1, jump.page), tp);
			pendingScrollJumpRef.current = null;
		}
		const start = (safePage - 1) * per;
		setPagination((prev) => ({
			...prev,
			total: list.length,
			total_pages: tp,
			page: safePage,
		}));
		setProducts(list.slice(start, start + per));
	}, [paginationSourceList, rampColorDrilldown?.key, brokenFilter, availableFilter, fullSizeSetFilter, pagination.per_page, pagination.page]);

	const openColorContributionView = useCallback(
		(fromView, brandForFilter) => {
			const brandArg = fromView === "categories" ? brandForFilter : null;
			const colorContributions = calculateColorContributions(allProducts, brandArg);
			const colorContributionsTinw = calculateColorContributionsTinw(allProducts, brandArg);
			const colorContributionsSoh = SOH_COLOR_RAMP_ENABLED
				? calculateColorContributionsSoh(allProducts, brandArg)
				: [];
			setGpContributionCard((prev) => ({ ...prev, transitioning: true }));
			setTimeout(() => {
				setGpContributionCard((prev) => ({
					...prev,
					colorContributions,
					colorContributionsTinw,
					colorContributionsSoh,
					viewMode: "colors",
					colorViewFrom: fromView,
					transitioning: false,
				}));
			}, 150);
		},
		[allProducts, calculateColorContributions, calculateColorContributionsTinw, calculateColorContributionsSoh]
	);

	const handleBackFromColors = useCallback(() => {
		setGpContributionCard((prev) => ({ ...prev, transitioning: true }));
		setTimeout(() => {
			setGpContributionCard((prev) => ({
				...prev,
				viewMode: prev.colorViewFrom || "brands",
				colorContributions: [],
				colorContributionsTinw: [],
				colorContributionsSoh: [],
				colorViewFrom: null,
				transitioning: false,
			}));
		}, 150);
	}, []);

	// GP Contribution card handlers
	const handleGpContributionClick = useCallback(
		(event) => {
			event.stopPropagation();

			// Toggle card if clicking on the same element
			if (gpContributionCard.open) {
				closeGpContributionCard();
				return;
			}

			setGpContributionAnchorEl(event.currentTarget);

			// Check how many brands are selected
			const selectedBrands = selectedItems.brands || new Set();
			const selectedBrandCount = selectedBrands.size;

			// If single brand is selected, go directly to category split level
			if (selectedBrandCount === 1) {
				const selectedBrand = Array.from(selectedBrands)[0];
				const categoryContributions = calculateCategoryContributions(allProducts, selectedBrand);

				setGpContributionCard({
					open: true,
					brandContributions: [],
					categoryContributions: categoryContributions,
					colorContributions: [],
					colorContributionsTinw: [],
					colorContributionsSoh: [],
					selectedBrand: selectedBrand,
					viewMode: "categories",
					colorViewFrom: null,
					transitioning: false,
				});
			} else {
				// If multiple brands or no brands selected, start with brand view
				const brandContributions = calculateBrandContributions(allProducts);
				setGpContributionCard({
					open: true,
					brandContributions: brandContributions,
					categoryContributions: [],
					colorContributions: [],
					colorContributionsTinw: [],
					colorContributionsSoh: [],
					selectedBrand: null,
					viewMode: "brands",
					colorViewFrom: null,
					transitioning: false,
				});
			}
		},
		[allProducts, calculateBrandContributions, gpContributionCard.open, selectedItems.brands, closeGpContributionCard]
	);

	const handleGpContributionMouseLeave = useCallback(() => {
		// Remove this handler since we're using click-based tooltips now
	}, []);

	const handleBrandClick = useCallback(
		(brand) => {
			// Show category split for the selected brand
			const categoryContributions = calculateCategoryContributions(allProducts, brand);

			// Start transition
			setGpContributionCard((prev) => ({
				...prev,
				transitioning: true,
			}));

			// After transition delay, switch to category view
			setTimeout(() => {
				setGpContributionCard((prev) => ({
					...prev,
					categoryContributions: categoryContributions,
					colorContributions: [],
					colorContributionsTinw: [],
					colorContributionsSoh: [],
					colorViewFrom: null,
					selectedBrand: brand,
					viewMode: "categories",
					transitioning: false,
				}));
			}, 150); // Half of the transition duration
		},
		[allProducts, calculateCategoryContributions]
	);

	const handleCategoryClick = useCallback(
		(category) => {
			setRampColorDrilldown(null);
			// Set both brand and department filters
			const newSelectedItems = {
				...selectedItems,
				departments: new Set([category]),
			};
			setSelectedItems(newSelectedItems);

			// Update filters - use department instead of subCategory
			const newFilters = {
				...filters,
				department: category,
			};
			setFilters(newFilters);

			// Check if we have cached data for this department
			const cacheKey = `department_${category}`;
			if (productCache.has(cacheKey)) {
				const cachedData = productCache.get(cacheKey);
				const cacheAge = Date.now() - cachedData.timestamp;
				const CACHE_EXPIRY = 10 * 60 * 1000; // 10 minutes - increased cache time

				if (cacheAge < CACHE_EXPIRY) {
					console.log("Using cached data for department:", category);
					setAllProducts(cachedData.allProducts);
					setPagination((prev) => ({ ...prev, page: 1, isPageChange: false }));
					setError("");
					setLoading(false); // Ensure loading is false
					return; // Exit early to prevent API call
				} else {
					console.log("Cache expired for department:", category, "age:", cacheAge);
					// Remove expired cache entry
					setProductCache((prev) => {
						const newCache = new Map(prev);
						newCache.delete(cacheKey);
						return newCache;
					});
				}
			}

			// If no cached data, filter from current allProducts if available
			if (allProducts.length > 0) {
				console.log("Filtering from current products for department:", category);
				const filteredProducts = allProducts.filter(
					(product) => product.department && product.department.toLowerCase() === category.toLowerCase()
				);

				if (filteredProducts.length > 0) {
					setAllProducts(filteredProducts);
					setPagination((prev) => ({
						...prev,
						page: 1,
						isPageChange: false,
						total_pages: Math.ceil(filteredProducts.length / pagination.per_page),
						total: filteredProducts.length,
					}));
					setError("");
					setLoading(false);

					// Cache the filtered results
					const cacheData = {
						products: filteredProducts.slice(0, pagination.per_page),
						allProducts: filteredProducts,
						timestamp: Date.now(),
					};
					setProductCache((prev) => new Map(prev).set(cacheKey, cacheData));
					console.log("Cached filtered data for department:", category);
					return; // Exit early to prevent API call
				}
			}

			// Only trigger API call if no valid cache
			// Trigger search by setting loading to true
			setLoading(true);
			setPagination((prev) => ({ ...prev, page: 1, isPageChange: false }));
			setError("");

			closeGpContributionCard();
		},
		[selectedItems, filters, productCache, allProducts, pagination.per_page]
	);

	const handleBackToBrands = useCallback(() => {
		// Start transition
		setGpContributionCard((prev) => ({
			...prev,
			transitioning: true,
		}));

		// After transition delay, switch to brand view
		setTimeout(() => {
			const brandContributions = calculateBrandContributions(allProducts);
			setGpContributionCard((prev) => ({
				...prev,
				brandContributions: brandContributions,
				categoryContributions: [],
				colorContributions: [],
				colorContributionsTinw: [],
				colorContributionsSoh: [],
				colorViewFrom: null,
				selectedBrand: null,
				viewMode: "brands",
				transitioning: false,
			}));
		}, 150); // Half of the transition duration
	}, [allProducts, calculateBrandContributions]);

	// Global flag to prevent duplicate calls in StrictMode
	window.filterOptionsLoaded = window.filterOptionsLoaded || false;

	// Cache for filter dependencies to prevent duplicate calls

	// Global flag to prevent duplicate filter-options calls across components
	window.filterOptionsCallInProgress = window.filterOptionsCallInProgress || false;

	const fetchFilterOptions = useCallback(async (startDate, endDate) => {
		if (filterOptionsLoadedRef.current || window.filterOptionsLoaded) {
			return;
		}

		// Prevent concurrent calls
		if (window.filterOptionsCallInProgress) {
			return;
		}

		try {
			setFilterOptionsLoading(true);
			window.filterOptionsCallInProgress = true;

			const params = {};
			if (startDate) params.start_date = format(startDate, "yyyy-MM-dd");
			if (endDate) params.end_date = format(endDate, "yyyy-MM-dd");
			const response = await pythonVmInstance.get("/st_pictorial/filter-options", { params });

			if (!response || !response.data) {
				console.error("Invalid API response:", response);
				throw new Error("Invalid API response");
			}

			const data = response.data;

			// The backend now returns data in the correct format
			const filterData = {
				departments: data.departments || [],
				subCategories: data.subCategories || [],
				brands: data.brands || [],
				subBrands: data.subBrands || [],
				styles: data.styles || [],
				colors: data.colors || [],
				sleeves: data.sleeves || [],
				divisions: data.divisions || [],
				seasons: data.seasons || [],
				fashionability: data.fashionability || [],
				occasions: data.occasions || [],
				palettes: data.palettes || [],
				fitTypes: data.fitTypes || [],
				fabricBlends: data.fabricBlends || [],
				nods: data.nods || [],
				channels: data.channels || [],
				stores: data.stores || [],
				cities: data.cities || [],
				states: data.states || [],
				regions: data.regions || [],
				tiers: data.tiers || [],
				aging: data.aging || [
					"0-30 days",
					"31-60 days",
					"61-90 days",
					"91-120 days",
					"121-180 days",
					"181-365 days",
					"365+ days",
				],
				st_percentage: data.st_percentage || [
					"0-10%",
					"11-20%",
					"21-30%",
					"31-40%",
					"41-50%",
					"51-60%",
					"61-70%",
					"71-80%",
					"81-90%",
					"91-100%",
				],
			};

			console.log("Setting filter options with data:", filterData);
			console.log("Channels in filterData:", filterData.channels);
			console.log("Tiers in filterData:", filterData.tiers);

			setFilterOptions((prev) => ({
				...prev,
				...filterData,
			}));

			setMasterFilterOptions(filterData);
			console.log("Filter options set, masterFilterOptions:", filterData);
			console.log("Master filter options channels:", filterData.channels);
			console.log("Master filter options tiers:", filterData.tiers);

			filterOptionsLoadedRef.current = true;
			window.filterOptionsLoaded = true;
		} catch (err) {
			console.error("Error fetching filter options:", err);
			setError("Failed to load filter options. Please refresh the page.");
		} finally {
			setFilterOptionsLoading(false);
			window.filterOptionsCallInProgress = false;
		}
	}, []);

	const mapFilterDependenciesToOptions = useCallback((data) => {
		const master = masterFilterOptions || {};
		return {
			departments: data.departments || [],
			subCategories: data.subCategories || [],
			brands: data.brands || [],
			subBrands: data.subBrands || [],
			styles: data.styles || [],
			colors: data.colors || [],
			sleeves: data.sleeves || [],
			divisions: data.divisions || [],
			seasons: data.seasons || [],
			fashionability: data.fashionability || [],
			occasions: data.occasions || [],
			palettes: data.palettes || [],
			fitTypes: data.fitTypes || [],
			fabricBlends: data.fabricBlends || [],
			nods: data.nods || master.nods || [],
			channels: data.channels || [],
			stores: data.stores || [],
			cities: data.cities || [],
			states: data.states || [],
			regions: data.regions || [],
			tiers: data.tiers || [],
			aging: master.aging || filterOptions.aging || [],
			st_percentage: master.st_percentage || filterOptions.st_percentage || [],
		};
	}, [masterFilterOptions, filterOptions.aging, filterOptions.st_percentage]);

	const fetchFilterDependencies = useCallback(async (activeFilters) => {
		if (!filterOptionsLoadedRef.current) return;

		const params = {};
		const skipKeys = new Set(["start_date", "end_date", "sort_field", "sort_direction"]);
		Object.entries(activeFilters || {}).forEach(([key, value]) => {
			if (skipKeys.has(key)) return;
			if (value && String(value).trim() !== "") {
				params[key] = value;
			}
		});

		if (filterDepsAbortRef.current) {
			filterDepsAbortRef.current.abort();
		}
		const abortController = new AbortController();
		filterDepsAbortRef.current = abortController;
		const seq = ++filterDepsSeqRef.current;

		try {
			const response = await pythonVmInstance.get("/st_pictorial/filter-dependencies", {
				params,
				signal: abortController.signal,
			});
			if (seq !== filterDepsSeqRef.current) return;

			const narrowed = mapFilterDependenciesToOptions(response.data || {});
			setFilterOptions((prev) => ({ ...prev, ...narrowed }));

			const fieldToOptionsKey = {
				department: "departments",
				subCategory: "subCategories",
				brand: "brands",
				subBrand: "subBrands",
				style: "styles",
				color: "colors",
				sleeve: "sleeves",
				division: "divisions",
				season: "seasons",
				fashionability: "fashionability",
				occasion: "occasions",
				palette: "palettes",
				fitType: "fitTypes",
				fabricBlend: "fabricBlends",
				nod: "nods",
				channel: "channels",
				store: "stores",
				city: "cities",
				state: "states",
				region: "regions",
				tier: "tiers",
			};

			setSelectedItems((prev) => {
				const next = { ...prev };
				let changed = false;
				Object.entries(fieldToOptionsKey).forEach(([field, optionsKey]) => {
					const allowed = new Set(narrowed[optionsKey] || []);
					if (allowed.size === 0) return;
					const current = prev[field] || new Set();
					const pruned = new Set([...current].filter((v) => allowed.has(v)));
					if (pruned.size !== current.size) {
						changed = true;
						next[field] = pruned;
					}
				});
				if (!changed) return prev;

				const updatedFilters = { ...filtersRef.current };
				Object.entries(fieldToOptionsKey).forEach(([field]) => {
					const setVal = next[field];
					updatedFilters[field] = setVal && setVal.size > 0 ? Array.from(setVal).join(",") : "";
				});
				setFilters(updatedFilters);
				return next;
			});
		} catch (err) {
			if (err?.code === "ERR_CANCELED" || err?.name === "CanceledError") return;
			console.error("Error fetching filter dependencies:", err);
		}
	}, [mapFilterDependenciesToOptions]);

	const scheduleFilterDependencies = useCallback(
		(activeFilters) => {
			if (filterDepsDebounceRef.current) {
				clearTimeout(filterDepsDebounceRef.current);
			}
			filterDepsDebounceRef.current = setTimeout(() => {
				fetchFilterDependencies(activeFilters);
			}, FILTER_DEPS_DEBOUNCE_MS);
		},
		[fetchFilterDependencies]
	);

	useEffect(() => {
		return () => {
			if (filterDepsDebounceRef.current) clearTimeout(filterDepsDebounceRef.current);
			if (filterDepsAbortRef.current) filterDepsAbortRef.current.abort();
		};
	}, []);

	// Fetch company totals; respects channel, store, city, state, region, tier so header totals match grid
	const fetchCompanyTotals = useCallback(async (startDate, endDate, filtersObj = {}) => {
		try {
			const formattedStartDate = format(startDate, "yyyy-MM-dd");
			const formattedEndDate = format(endDate, "yyyy-MM-dd");
			const params = { start_date: formattedStartDate, end_date: formattedEndDate };
			const locationKeys = ["channel", "store", "city", "state", "region", "tier"];
			locationKeys.forEach((key) => {
				const v = filtersObj[key];
				if (v != null && String(v).trim() !== "") params[key] = String(v).trim();
			});

			const response = await pythonVmInstance.get("/st_pictorial/company-totals", {
				params,
			});

			if (response.data) {
				setCompanyTotals({
					totalBuy: response.data.totalBuy ?? 0,
					totalSold: response.data.totalSold ?? 0,
					totalSoldOverall: response.data.totalSoldOverall ?? 0,
					balance: response.data.balance ?? 0,
					periodStPct: response.data.periodStPct ?? 0,
					totalStPct: response.data.totalStPct ?? 0,
					gpValue: response.data.gpValue ?? 0,
					mrpBuyValue: response.data.mrpBuyValue ?? 0,
					mrpSoldValue: response.data.mrpSoldValue ?? 0,
				});
			}
		} catch (err) {
			console.error("Error fetching company totals:", err);
			setCompanyTotals({
				totalBuy: 0,
				totalSold: 0,
				totalSoldOverall: 0,
				balance: 0,
				periodStPct: 0,
				totalStPct: 0,
				gpValue: 0,
				mrpBuyValue: 0,
				mrpSoldValue: 0,
			});
		}
	}, []);

	const fetchProducts = useCallback(
		async (startDate, endDate) => {
			// Increment request ID at the function scope
			const thisRequestId = ++currentRequestIdRef.current;
			setDataLoadPhase("fetching");

			// Start progress simulation for long queries
			setQueryProgress(10);
			const progressInterval = setInterval(() => {
				setQueryProgress((prev) => Math.min(prev + 10, 90));
			}, 500);

			try {
				// Don't set loading here - it's already set by the calling function
				setError("");

				const formattedStartDate = format(startDate, "yyyy-MM-dd");
				const formattedEndDate = format(endDate, "yyyy-MM-dd");

				const params = {
					start_date: formattedStartDate,
					end_date: formattedEndDate,
				};

				// Add filters to params (excluding sort parameters for client-side sorting)
				Object.entries(filtersRef.current).forEach(([key, value]) => {
					if (
						key !== "start_date" &&
						key !== "end_date" &&
						key !== "sort_field" &&
						key !== "sort_direction" &&
						value &&
						value.trim() !== ""
					) {
						params[key] = value;
					}
				});

				const response = await pythonVmInstance.get("/st_pictorial/products", {
					params: params,
					timeout: 300000, // 5 minutes to match backend
				});

				// Check if this is still the latest request
				if (thisRequestId !== currentRequestIdRef.current) {
					return;
				}

				if (!response.data || !response.data.products) {
					throw new Error("Invalid response format from server");
				}

				setMetricsChannel(response.data.metrics_channel ?? null);

				const flattenedProducts = response.data.products.flat();

				if (flattenedProducts.length === 0) {
					setDataLoadPhase(null);
					setError("No data found for the selected filters. Please try changing the date range or filters.");
					setProducts([]);
					setAllProducts([]);
					setPagination((prev) => ({
						...prev,
						total_pages: 0,
						total: 0,
					}));
					return;
				}

				setDataLoadPhase("rendering");
				// Let the browser paint the "rendering" overlay before heavy JS (sort, totals, slice)
				await new Promise((resolve) => requestAnimationFrame(() => resolve()));

				if (thisRequestId !== currentRequestIdRef.current) {
					return;
				}

				// Process products only if this is still the latest request
				if (thisRequestId === currentRequestIdRef.current) {
					// The backend now returns products in the correct format
					const transformedProducts = flattenedProducts.map((product) => ({
						...product,
						// Ensure all required fields exist with defaults
						troco_ls: product.troco_ls || {
							t_buy: 0,
							t_inw: 0,
							t_sale: 0,
							st: 0,
							mrp: "0",
						},
						lcogs: product.lcogs || {
							gp_percentage: "0.00%",
							gp_value: "0.00",
							oln_cont: "0%",
							offln_cont: "0%",
							sales_cont: "0%",
						},
					}));

					console.log("Setting products:", {
						transformedCount: transformedProducts.length,
						perPage: pagination.per_page,
						totalPages: Math.ceil(transformedProducts.length / pagination.per_page),
					});

					// Apply default sorting to the products
					const sortedProducts = sortProducts(transformedProducts, sortConfig.field, sortConfig.direction);

					setRampColorDrilldown(null);
					setAllProducts(sortedProducts);

					const totalPages = Math.ceil(sortedProducts.length / pagination.per_page);
					setPagination((prev) => ({
						...prev,
						total_pages: totalPages,
						total: sortedProducts.length,
						page: 1, // Reset to first page on new data
					}));

					const currentPageProducts = sortedProducts.slice(0, pagination.per_page);
					console.log("Setting current page products:", currentPageProducts.length);

					// Cache the results if a department filter is applied
					if (filtersRef.current.department) {
						const cacheKey = `department_${filtersRef.current.department}`;
						const cacheData = {
							products: currentPageProducts,
							allProducts: sortedProducts,
							timestamp: Date.now(),
						};
						setProductCache((prev) => new Map(prev).set(cacheKey, cacheData));
						console.log("Cached data for department:", filtersRef.current.department);
					}

					if (sortedProducts.length > 0) {
						const totalBuy = sortedProducts.reduce((sum, product) => sum + (product.troco_ls?.t_buy || 0), 0);
						const totalSold = sortedProducts.reduce((sum, product) => sum + (product.troco_ls?.t_sale || 0), 0);
						const totalSoldLifetime = sortedProducts.reduce(
							(sum, product) => sum + (product.troco_ls?.t_sale_total || 0),
							0
						);

						// Calculate averages and totals for the new metrics
						const totalCOGS = sortedProducts.reduce((sum, product) => sum + parseFloat(product.COGS || 0), 0);
						const totalGPValue = sumGpValueForProducts(sortedProducts);
						const totalGPPercentage = sortedProducts.reduce(
							(sum, product) => sum + parseFloat(product.lcogs?.gp_percentage?.replace("%", "") || 0),
							0
						);

						const avgCOGS = sortedProducts.length > 0 ? totalCOGS / sortedProducts.length : 0;
						const avgGPPercentage =
							sortedProducts.length > 0 ? Math.round((totalGPPercentage / sortedProducts.length) * 100) / 100 : 0;

						// Calculate ASP (Average Selling Price) based on realised value per unit sold
						const totalRealisedValue = sortedProducts.reduce(
							(sum, product) => sum + parseFloat(product.realised_value || 0),
							0
						);
						const totalSoldQty = sortedProducts.reduce((sum, product) => sum + (product.troco_ls?.t_sale || 0), 0);

						// Calculate ASP as total realised value / total sold quantity
						let avgASP = 0;
						if (totalSoldQty > 0 && totalRealisedValue > 0) {
							avgASP = totalRealisedValue / totalSoldQty;
						}

						// Exact portfolio sums, then rounded avg MRP × qty for displayed MRP Value
						const mrpValueExact = transformedProducts.reduce(
							(sum, product) => sum + parseFloat(product.troco_ls?.mrp || 0) * (product.troco_ls?.t_buy || 0),
							0
						);
						const soldMRPValueExact = transformedProducts.reduce(
							(sum, product) => sum + parseFloat(product.troco_ls?.mrp || 0) * (product.troco_ls?.t_sale || 0),
							0
						);

						const buyCogsValue = transformedProducts.reduce((sum, product) => {
							const cogs = parseFloat(String(product.COGS ?? "").replace(/,/g, "")) || 0;
							return sum + cogs * (product.troco_ls?.t_buy || 0);
						}, 0);
						const soldCogsValue = transformedProducts.reduce((sum, product) => {
							const cogs = parseFloat(String(product.COGS ?? "").replace(/,/g, "")) || 0;
							return sum + cogs * (product.troco_ls?.t_sale || 0);
						}, 0);

						const avgMRPBuy = totalBuy > 0 ? Math.round(mrpValueExact / totalBuy) : 0;
						const mrpValue = avgMRPBuy * totalBuy;
						const avgMRPSold = totalSold > 0 ? Math.round(soldMRPValueExact / totalSold) : 0;
						const soldMRPValue = avgMRPSold * totalSold;

						const buyAvgCOGS = totalBuy > 0 ? buyCogsValue / totalBuy : 0;
						const buyMulti = buyCogsValue > 0 ? mrpValue / buyCogsValue : 0;
						const soldMulti = soldCogsValue > 0 ? soldMRPValue / soldCogsValue : 0;

						const percentageSold = totalBuy > 0 ? (totalSoldLifetime / totalBuy) * 100 : 0;

						// Sales Contribution (Category Level) - Calculate against company total
						const salesContribution = companyTotals.totalSold > 0 ? (totalSold / companyTotals.totalSold) * 100 : 100;

						// Fix: Cap the GP contribution at 100% to prevent unrealistic percentages
						// This happens when filtered data exceeds company totals due to data inconsistencies
						const rawGpContribution = companyTotals.gpValue > 0 ? (totalGPValue / companyTotals.gpValue) * 100 : 0;

						const gpContribution = Math.min(Math.round(rawGpContribution * 10) / 10, 100);

						setTotals({
							totalBuy: totalBuy,
							totalSold: totalSold,
							totalSoldLifetime: totalSoldLifetime,
							subCategory: filters.subCategory || "All",
							avgASP: avgASP,
							avgMRP: avgMRPBuy,
							avgMRPSold: avgMRPSold,
							buyAvgCOGS: buyAvgCOGS,
							avgCOGS: avgCOGS,
							gpValue: totalGPValue,
							gpPercentage: avgGPPercentage,
							mrpValue: mrpValue,
							soldMRPValue: soldMRPValue,
							buyCogsValue: buyCogsValue,
							soldCogsValue: soldCogsValue,
							buyMulti: buyMulti,
							soldMulti: soldMulti,
							percentageSold: percentageSold,
							salesContribution: salesContribution,
							gpContribution: gpContribution,
						});
					}
				}

				if (thisRequestId === currentRequestIdRef.current) {
					setDataLoadPhase(null);
				}
			} catch (err) {
				console.error("Error in fetchProducts:", err);
				if (err.response && err.response.status === 500) {
					setError("No data found for the selected filters. Please try changing the date range or filters.");
				} else {
					setError(`Failed to fetch products: ${err.message}`);
				}
				setProducts([]);
					setAllProducts([]);
					setMetricsChannel(null);
					setRampColorDrilldown(null);
					setPagination((prev) => ({
						...prev,
						total_pages: 0,
						total: 0,
					}));
				if (currentRequestIdRef.current === thisRequestId) {
					setDataLoadPhase(null);
				}
			} finally {
				// Clear progress interval and complete progress
				clearInterval(progressInterval);
				setQueryProgress(100);

				// Only update loading state if this is the latest request
				if (currentRequestIdRef.current === thisRequestId) {
					setLoading(false);
					setDataLoadPhase(null);
					// Reset progress after a short delay
					setTimeout(() => setQueryProgress(0), 500);
				}
			}
		},
		[pagination.per_page, companyTotals]
	);

	// Clean up cancel token on unmount
	useEffect(() => {
		return () => {
			if (cancelTokenRef.current) {
				cancelTokenRef.current.cancel("Component unmounted");
			}
		};
	}, []);

	// Master dropdown lists: load once on mount. Backend `sales_filter_snapshot` is not date-filtered in SQL;
	// changing the report period does not require refetching these options. Backend also applies a 5-minute cache.
	useEffect(() => {
		fetchFilterOptions(dateRange.startDate, dateRange.endDate);

		return () => {
			window.filterOptionsCallInProgress = false;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: avoid refetch on every date change
	}, [fetchFilterOptions]);

	// Fetch company totals when date range or location filters change (channel, store, city, state, region, tier)
	useEffect(() => {
		if (products.length > 0) {
			fetchCompanyTotals(dateRange.startDate, dateRange.endDate, filters);
		}
	}, [dateRange.startDate, dateRange.endDate, filters.channel, filters.store, filters.city, filters.state, filters.region, filters.tier, products.length, fetchCompanyTotals]);

	// Fetch products when date range or filters change
	useEffect(() => {
		console.log("Products useEffect triggered:", {
			loading,
			isPageChange: pagination.isPageChange,
			isApplyingFilters: isApplyingFiltersRef.current,
			dateRange: dateRange.startDate.toISOString(),
			filtersCount: Object.keys(filters).length,
		});

		// Only fetch if loading is true and it's not a pagination change and not in the middle of applying filters
		if (loading && !pagination.isPageChange && !isApplyingFiltersRef.current) {
			console.log("Calling fetchProducts...");
			// Fetch immediately without delay for fastest results
			fetchProducts(dateRange.startDate, dateRange.endDate);
		}
	}, [dateRange.startDate, dateRange.endDate, filters, loading, pagination.isPageChange]);

	// Cache cleanup effect - runs every 10 minutes to clean up expired entries
	useEffect(() => {
		const cleanupInterval = setInterval(() => {
			const CACHE_EXPIRY = 10 * 60 * 1000; // 10 minutes
			const now = Date.now();

			setProductCache((prev) => {
				const newCache = new Map();
				let cleaned = false;

				for (const [key, value] of prev) {
					if (now - value.timestamp < CACHE_EXPIRY) {
						newCache.set(key, value);
					} else {
						cleaned = true;
					}
				}

				if (cleaned) {
					console.log("Cleaned up expired cache entries");
				}

				return newCache;
			});
		}, 10 * 60 * 1000); // Run every 10 minutes

		return () => clearInterval(cleanupInterval);
	}, []);

	// Set default sort configuration for client-side sorting
	useEffect(() => {
		setSortConfig({
			field: "period_st",
			direction: "desc",
		});
	}, []);

	// When filters are cleared, reset dropdown options to full list (from filter-options)
	useEffect(() => {
		if (!filterOptionsLoadedRef.current) return;
		const hasActiveFilters = Object.entries(filters).some(
			([key, value]) =>
				value &&
				value.trim() !== "" &&
				key !== "start_date" &&
				key !== "end_date" &&
				key !== "sort_field" &&
				key !== "sort_direction"
		);
		if (!hasActiveFilters && Object.keys(masterFilterOptions).length > 0) {
			setFilterOptions(masterFilterOptions);
		}
	}, [filters, masterFilterOptions]);

	// Close dropdowns and tooltips when clicking outside
	useEffect(() => {
		const handleClickOutside = (event) => {
			// Close dropdowns
			if (openDropdowns.size > 0) {
				// Check if click is outside any dropdown
				const isClickInsideDropdown = event.target.closest("[data-dropdown]");
				if (!isClickInsideDropdown) {
					setOpenDropdowns(new Set());
				}
			}

			// Popover handles outside clicks via onClose; keep for legacy nodes if any
			if (gpContributionCard.open) {
				const isClickInsideCard = event.target.closest("[data-gp-card]");
				const isClickOnGpContribution = event.target.closest("[data-gp-contribution]");
				const isClickInsidePopover = event.target.closest(".MuiPopover-root");
				if (!isClickInsideCard && !isClickOnGpContribution && !isClickInsidePopover) {
					closeGpContributionCard();
				}
			}
		};

		document.addEventListener("mousedown", handleClickOutside);
		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, [openDropdowns, gpContributionCard.open, closeGpContributionCard]);

	// Trigger dependency filtering when a dropdown closes and filters have changed
	useEffect(() => {
		if (openDropdowns.size > 0 && prevOpenDropdownsSize.current === 0) {
			// Dropdown just opened
			filtersWhenDropdownOpened.current = JSON.stringify(filters);
		} else if (openDropdowns.size === 0 && prevOpenDropdownsSize.current > 0) {
			// Dropdown just closed
			const currentFiltersStr = JSON.stringify(filters);
			if (filtersWhenDropdownOpened.current !== currentFiltersStr && filterOptionsLoadedRef.current) {
				const hasActiveFilters = Object.entries(filters).some(
					([key, value]) =>
						value &&
						String(value).trim() !== "" &&
						!["start_date", "end_date", "sort_field", "sort_direction"].includes(key)
				);

				if (!hasActiveFilters) {
					setFilterOptions(masterFilterOptions);
				} else {
					scheduleFilterDependencies(filters);
				}
			}
		}
		prevOpenDropdownsSize.current = openDropdowns.size;
	}, [openDropdowns.size, filters, masterFilterOptions, scheduleFilterDependencies]);

	const handleStartDateChange = (newDate) => {
		setPendingDateRange((prev) => ({
			...prev,
			startDate: newDate,
		}));
	};

	const handleEndDateChange = (newDate) => {
		setPendingDateRange((prev) => ({
			...prev,
			endDate: newDate,
		}));
	};

	const handlePageChange = (event, newPage) => {
		setPagination((prev) => ({ ...prev, page: newPage }));
	};

	// Defer parent/list update so typing stays instant (input updates sync, list update in transition)
	const setSearchTermsDeferred = useCallback((field, value) => {
		startTransition(() => {
			setSearchTerms((prev) => ({ ...prev, [field]: value }));
		});
	}, []);

	// Update the handleSearchTermChange function to be more robust (still used for instant display in some paths)
	const handleSearchTermChange = (field, value) => {
		setSearchTerms((prev) => ({
			...prev,
			[field]: value,
		}));
	};

	/** Selected values first (stable order within each group) so open dropdowns show current choices at the top. */
	const orderOptionsSelectedFirst = (optionsList, selected) => {
		if (!optionsList?.length || !selected || selected.size === 0) return optionsList;
		const atTop = [];
		const rest = [];
		for (const o of optionsList) {
			if (selected.has(o)) atTop.push(o);
			else rest.push(o);
		}
		return [...atTop, ...rest];
	};

	// Update the getFilteredOptions function to be more consistent
	const getFilteredOptions = (field) => {
		const mappedKey = filterKeyMap[field] || (field.endsWith("s") ? field : `${field}s`);
		const sel = selectedItems[mappedKey] || new Set();

		// Special case for fashionability
		if (field === "fashionability") {
			const searchTerm = (searchTerms[field] || "").toLowerCase();
			const options = filterOptions[field] || [];
			const raw = options.filter((option) => option && option.toLowerCase().includes(searchTerm));
			return orderOptionsSelectedFirst(raw, sel);
		}

		// For other fields
		const optionsKey = field.endsWith("s") ? field : `${field}s`;
		const searchTerm = (searchTerms[field] || "").toLowerCase();
		const options = filterOptions[optionsKey] || [];

		const raw = options.filter((option) => option && option.toLowerCase().includes(searchTerm.toLowerCase()));

		return orderOptionsSelectedFirst(raw, sel);
	};

	// Update the handleFilterChange function
	const handleFilterChange = (event) => {
		const { name, value, type } = event.target;

		const mappedKey = filterKeyMap[name] || `${name}s`;
		const apiKey = name;

		if (type === "checkbox") {
			setSelectedItems((prev) => {
				const currentSet = new Set(prev[mappedKey] || []);
				if (currentSet.has(value)) {
					currentSet.delete(value);
				} else {
					currentSet.add(value);
				}
				setFilters((prevFilters) => ({
					...prevFilters,
					[apiKey]: currentSet.size > 0 ? Array.from(currentSet).join(",") : "",
				}));
				return { ...prev, [mappedKey]: currentSet };
			});
			return;
		}

		if (value === "") {
			setFilters((prev) => ({ ...prev, [apiKey]: "" }));
			setSelectedItems((prev) => ({ ...prev, [mappedKey]: new Set() }));
			return;
		}

		// For single select, replace the value but preserve other filters
		setFilters((prev) => ({ ...prev, [apiKey]: value }));
		setSelectedItems((prev) => ({ ...prev, [mappedKey]: new Set([value]) }));
	};

	// Add getSelectDisplayValue function
	const getSelectDisplayValue = (field) => {
		const key = filterKeyMap[field] || `${field}s`;
		const selectedSet = selectedItems[key];
		if (!selectedSet || selectedSet.size === 0) return "";

		// Show count if multiple items are selected
		if (selectedSet.size > 1) {
			return `${selectedSet.size} selected`;
		}

		// Show the single item if only one is selected
		return Array.from(selectedSet).join(", ");
	};

	// Add this mapping above createFilterSelect
	const filterKeyMap = {
		brand: "brands",
		subBrand: "subBrands",
		department: "departments",
		season: "seasons",
		city: "cities",
		subCategory: "subCategories",
		palette: "palettes",
		fashionability: "fashionability",
		fitType: "fitTypes",
		fabricBlend: "fabricBlends",
		nod: "nods",
		channel: "channels",
		store: "stores",
		state: "states",
		region: "regions",
		tier: "tiers",
		aging: "aging",
		st_percentage: "st_percentage",
		// add more if needed
	};

	// Function to generate dynamic contribution labels based on selected filters
	const getContributionLabel = (type) => {
		const activeFilters = Object.entries(filters).filter(
			([key, value]) =>
				value && value !== "" && key !== "start_date" && key !== "end_date" && key !== "sort_field" && key !== "sort_direction"
		);

		if (activeFilters.length === 0) {
			return `${type}`;
		}

		// Brand abbreviations mapping
		const brandAbbreviations = {
			"Rare Rabbit": "RR",
			Rareism: "RSM",
			"Rare Ones": "RO",
			Rarez: "RZ",
		};

		// Get brand if selected
		const brandFilter = activeFilters.find(([key, value]) => key === "brand");
		const brandAbbr = brandFilter ? brandAbbreviations[brandFilter[1]] || brandFilter[1] : null;

		// Get season if selected
		const seasonFilter = activeFilters.find(([key, value]) => key === "season");
		const season = seasonFilter ? seasonFilter[1] : null;

		// Get department if selected
		const deptFilter = activeFilters.find(([key, value]) => key === "department");
		const department = deptFilter ? deptFilter[1] : null;

		// Build the label based on filter combinations
		let label = `${type} (`;

		if (brandAbbr && season && department) {
			// 3 filters: Brand + Season + Department
			label += `${brandAbbr}) - ${season} ${department}`;
		} else if (brandAbbr && season) {
			// 2 filters: Brand + Season
			label += `${brandAbbr}) - ${season}`;
		} else if (brandAbbr) {
			// 1 filter: Brand only
			label += `${brandAbbr})`;
		} else if (season) {
			// 1 filter: Season only
			label += `${season})`;
		} else if (department) {
			// 1 filter: Department only
			label += `${department})`;
		} else {
			// Other filter combinations
			const filterLabels = activeFilters.map(([key, value]) => {
				const keyLabel = key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, " $1");
				return `${keyLabel}: ${value}`;
			});
			label += filterLabels.join(", ") + ")";
		}

		return label;
	};

	// Lazy-load filter dropdowns: show first N by default; when user searches, show all matches
	const FILTER_DROPDOWN_INITIAL_LIMIT = 100;

	// Dropdown-style filter component with checkboxes
	const createDropdownFilter = (field, label, dependencies = []) => {
		const mappedKey = filterKeyMap[field] || `${field}s`;
		const options = masterFilterOptions[mappedKey] || [];
		const validOptions = filterOptions[mappedKey] || [];
		const selectedSet = selectedItems[mappedKey] || new Set();
		const isOpen = openDropdowns.has(field);

		// Filter options based on valid options (always show options, just filter them)
		const enabledOptions = options.filter((option) => validOptions.includes(option));

		// Get search term for this field
		const searchTerm = searchTerms[field] || "";
		const filteredOptionsRaw = enabledOptions.filter((option) =>
			option.toLowerCase().includes(searchTerm.toLowerCase())
		);
		const filteredOptions = orderOptionsSelectedFirst(filteredOptionsRaw, selectedSet);

		// Lazy display: without search show first 100 + any selected not in first 100; with search show all matches
		const hasSearch = searchTerm.trim() !== "";
		const optionsToDisplay =
			hasSearch
				? filteredOptions
				: (() => {
						const first = filteredOptions.slice(0, FILTER_DROPDOWN_INITIAL_LIMIT);
						const selectedNotInFirst = Array.from(selectedSet).filter(
							(o) => filteredOptions.includes(o) && !first.includes(o)
						);
						return [...first, ...selectedNotInFirst];
					})();
		const isTruncated = !hasSearch && filteredOptions.length > FILTER_DROPDOWN_INITIAL_LIMIT;

		const handleCheckboxChange = (option, checked) => {
			const newSet = new Set(selectedSet);
			if (checked) {
				newSet.add(option);
			} else {
				newSet.delete(option);
			}

			setSelectedItems((prev) => ({
				...prev,
				[mappedKey]: newSet,
			}));

			// Update filters immediately
			const updatedFilters = {
				...filters,
				[field]: newSet.size > 0 ? Array.from(newSet).join(",") : "",
			};
			setFilters(updatedFilters);
		};

		const handleSelectAll = () => {
			const selectedInDisplay = Array.from(selectedSet).filter((option) => optionsToDisplay.includes(option));
			const allSelected = optionsToDisplay.length > 0 && selectedInDisplay.length === optionsToDisplay.length;
			const newSet = allSelected ? new Set() : new Set(optionsToDisplay);

			setSelectedItems((prev) => ({
				...prev,
				[mappedKey]: newSet,
			}));

			// Update filters immediately
			const updatedFilters = {
				...filters,
				[field]: newSet.size > 0 ? Array.from(newSet).join(",") : "",
			};
			setFilters(updatedFilters);
		};

		// Only one dropdown open at a time for faster click and less DOM work
		const handleToggleDropdown = () => {
			setOpenDropdowns((prev) => (prev.has(field) ? new Set() : new Set([field])));
		};

		const getDisplayText = () => {
			if (selectedSet.size === 0) return `${label}`;
			if (selectedSet.size === 1) return Array.from(selectedSet)[0];
			return `${selectedSet.size} selected`;
		};

		return (
			<Box sx={{ position: "relative", width: "100%" }} data-dropdown>
				{/* Dropdown trigger button */}
				<Button
					onClick={handleToggleDropdown}
					variant="outlined"
					fullWidth
					sx={{
						justifyContent: "space-between",
						textTransform: "none",
						fontSize: "0.6875rem",
						height: "40px",
						backgroundColor: "#f8f8f8",
						borderColor: isOpen ? "#000000" : "#000000",
						borderWidth: isOpen ? "2px" : "1px",
						color: selectedSet.size > 0 ? "#000000" : "#666666",
						fontWeight: selectedSet.size > 0 ? 500 : 400,
						"&:hover": {
							borderColor: "#000000",
							borderWidth: "2px",
							backgroundColor: "#f0f0f0",
						},
						"& .MuiButton-endIcon": {
							transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
							transition: "transform 0.2s ease-in-out",
						},
					}}
					endIcon={<ExpandMoreIcon />}>
					{getDisplayText()}
				</Button>

				{/* Dropdown content */}
				{isOpen && (
					<Box
						sx={{
							position: "absolute",
							top: "100%",
							left: 0,
							right: 0,
							zIndex: 1300,
							backgroundColor: "#f8f8f8",
							border: "1px solid #666666",
							borderRadius: "4px",
							boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
							mt: 0.5,
							maxHeight: "300px",
							overflow: "hidden",
							display: "flex",
							flexDirection: "column",
						}}>
						{/* Search bar: local state for instant typing, debounced parent update to reduce lag */}
						{enabledOptions.length > 5 && (
							<Box sx={{ p: 1, borderBottom: "1px solid #e0e0e0" }}>
								<FilterSearchInput
									field={field}
									label={label}
									value={searchTerm}
									onSearchChangeDebounced={setSearchTermsDeferred}
									sx={{
										"& .MuiOutlinedInput-root": {
											fontSize: "0.6rem",
											height: "32px",
											backgroundColor: "#ffffff",
											"& fieldset": { borderColor: "#e0e0e0" },
											"&:hover fieldset": { borderColor: "#1976d2" },
											"&.Mui-focused fieldset": { borderColor: "#1976d2", borderWidth: 1 },
										},
									}}
								/>
							</Box>
						)}

						{/* Select All checkbox */}
						<Box
							sx={{
								display: "flex",
								alignItems: "center",
								px: 1,
								py: 0.5,
								borderBottom: "1px solid #666666",
								backgroundColor: "#ffffff",
							}}>
							<Checkbox
								checked={
									optionsToDisplay.length > 0 &&
									optionsToDisplay.length ===
										Array.from(selectedSet).filter((option) => optionsToDisplay.includes(option)).length
								}
								indeterminate={
									Array.from(selectedSet).filter((option) => optionsToDisplay.includes(option)).length > 0 &&
									Array.from(selectedSet).filter((option) => optionsToDisplay.includes(option)).length <
										optionsToDisplay.length
								}
								onChange={handleSelectAll}
								size="small"
								sx={{
									p: 0.5,
									color: "#666",
									"&.Mui-checked": {
										color: "#000000",
									},
								}}
							/>
							<Typography
								variant="body2"
								sx={{
									fontSize: "0.6rem",
									fontWeight: 500,
									color: "#666666",
									ml: 0.5,
								}}>
								Select All
							</Typography>
						</Box>

						{/* Hint when list is truncated (first 100 only; search to see more) */}
						{isTruncated && (
							<Box sx={{ px: 1, py: 0.5, backgroundColor: "#f5f5f5", borderBottom: "1px solid #e0e0e0" }}>
								<Typography variant="caption" sx={{ fontSize: "0.65rem", color: "#666" }}>
									Showing first {FILTER_DROPDOWN_INITIAL_LIMIT} of {filteredOptions.length}. Search to find more.
								</Typography>
							</Box>
						)}

						{/* Options list */}
						<Box
							sx={{
								maxHeight: "200px",
								overflow: "auto",
								"&::-webkit-scrollbar": {
									width: "6px",
								},
								"&::-webkit-scrollbar-track": {
									backgroundColor: "#f1f1f1",
								},
								"&::-webkit-scrollbar-thumb": {
									backgroundColor: "#c1c1c1",
									borderRadius: "3px",
								},
								"&::-webkit-scrollbar-thumb:hover": {
									backgroundColor: "#a8a8a8",
								},
							}}>
							{optionsToDisplay.length === 0 ? (
								<Typography
									variant="body2"
									sx={{
										color: "#666",
										fontStyle: "italic",
										textAlign: "center",
										py: 2,
										px: 1,
									}}>
									{searchTerm ? "No matching options" : "No options available"}
								</Typography>
							) : (
								optionsToDisplay.map((option) => (
									<Box
										key={option}
										sx={{
											display: "flex",
											alignItems: "center",
											py: 0.5,
											px: 1,
											"&:hover": {
												backgroundColor: "rgba(0, 0, 0, 0.04)",
											},
										}}>
										<Checkbox
											checked={selectedSet.has(option)}
											onChange={(e) => handleCheckboxChange(option, e.target.checked)}
											size="small"
											sx={{
												p: 0.5,
												color: "#666",
												"&.Mui-checked": {
													color: "#000000",
												},
											}}
										/>
										<Typography
											variant="body2"
											sx={{
												fontSize: "0.6rem",
												color: selectedSet.has(option) ? "#000000" : "#666666",
												fontWeight: selectedSet.has(option) ? 500 : 400,
												ml: 0.5,
												flex: 1,
												overflow: "hidden",
												textOverflow: "ellipsis",
												whiteSpace: "nowrap",
											}}>
											{option}
										</Typography>
									</Box>
								))
							)}
						</Box>
					</Box>
				)}
			</Box>
		);
	};

	// After the dependent filter group, render the rest of the filters independently
	const independentFilters = ["channel", "store", "city", "state", "region", "tier"];

	const handleSearch = useCallback(() => {
		setLoading(true);
		setError("");

		try {
			const formattedStartDate = format(pendingDateRange.startDate, "yyyy-MM-dd");
			const formattedEndDate = format(pendingDateRange.endDate, "yyyy-MM-dd");
			const newFilters = {
				start_date: formattedStartDate,
				end_date: formattedEndDate,
			};
			Object.entries(selectedItems).forEach(([field, items]) => {
				const apiKey = field.endsWith("s") ? field.slice(0, -1) : field;
				if (["subCategories", "cities", "fashionability"].includes(field)) {
					const specialKey = field === "subCategories" ? "subCategory" : field === "cities" ? "city" : "fashionability";
					if (items.size > 0) {
						newFilters[specialKey] = Array.from(items).join(",");
					} else {
						delete newFilters[specialKey];
					}
				} else {
					if (items.size > 0) {
						newFilters[apiKey] = Array.from(items).join(",");
					} else {
						delete newFilters[apiKey];
					}
				}
			});
			setFilters(newFilters);
			setDateRange(pendingDateRange);
			setPagination((prev) => ({ ...prev, page: 1 }));
			// Products and company totals will be fetched automatically by useEffect when dateRange changes
		} catch (err) {
			console.error("Error in handleSearch:", err);
			setError("Error applying filters");
		}
	}, [pendingDateRange, selectedItems]);

	/** Sold in period ÷ buy qty (matches product card Period ST). */
	const calculatePeriodST = useCallback(() => {
		if (totals.totalBuy === 0) return "0.00";
		return ((totals.totalSold / totals.totalBuy) * 100).toFixed(2);
	}, [totals.totalBuy, totals.totalSold]);

	/** Lifetime sell-through: total sold ÷ buy. */
	const calculateST = useCallback(() => {
		if (totals.totalBuy === 0) return "0.00";
		const soldForSt = totals.totalSoldLifetime ?? totals.totalSold;
		return ((soldForSt / totals.totalBuy) * 100).toFixed(2);
	}, [totals.totalBuy, totals.totalSold, totals.totalSoldLifetime]);

	// Format number without decimals — Indian numbering system (e.g. 10,00,000)
	const formatNumber = (num) => {
		return Math.round(num).toLocaleString("en-IN");
	};

	/** Per-piece averages; MRP uses 3 dp so Avg × Pcs is closer to Value than integer rounding. */
	const formatAvgRate = (num, fractionDigits = 2) => {
		if (num == null || !Number.isFinite(Number(num))) return "0";
		return Number(num).toLocaleString("en-IN", {
			minimumFractionDigits: fractionDigits,
			maximumFractionDigits: fractionDigits,
		});
	};

	/** Flash the orange highlight ring on a card already in the DOM */
	const flashCard = useCallback((pkey) => {
		const id = `pict-card-${String(pkey).replace(/[^a-zA-Z0-9]/g, "-")}`;
		const el = document.getElementById(id);
		if (!el) return;
		el.scrollIntoView({ behavior: "smooth", block: "center" });
		el.style.transition = "outline 0.15s ease, box-shadow 0.15s ease";
		el.style.outline = "3px solid #e65100";
		el.style.borderRadius = "8px";
		el.style.boxShadow = "0 0 0 5px rgba(230,81,0,0.25)";
		setTimeout(() => {
			el.style.outline = "";
			el.style.boxShadow = "";
			el.style.borderRadius = "";
		}, 1800);
	}, []);

	/** Navigate to the page that contains `pkey` then scroll to it */
	const navigateToProduct = useCallback((pkey) => {
		let idx = paginationSourceList.findIndex((p) => p.PKEY === pkey);
		if (idx === -1) {
			// Similar options are non-broken; they disappear from the list when WH/EBO filter is on
			const idxFull = allProductsForView.findIndex((p) => p.PKEY === pkey);
			if (idxFull === -1) return;
			const targetPage = Math.floor(idxFull / pagination.per_page) + 1;
			pendingScrollPkeyRef.current = pkey;
			pendingScrollJumpRef.current = { pkey, page: targetPage };
			setBrokenFilter(null);
			setAvailableFilter(null);
			setFullSizeSetFilter(null);
			return;
		}
		const targetPage = Math.floor(idx / pagination.per_page) + 1;
		if (targetPage === pagination.page) {
			flashCard(pkey);
		} else {
			pendingScrollPkeyRef.current = pkey;
			setPagination((prev) => ({ ...prev, page: targetPage }));
		}
	}, [paginationSourceList, allProductsForView, pagination.per_page, pagination.page, flashCard]);

	// After a page change renders, fire the pending scroll if the product is now visible
	useEffect(() => {
		const pkey = pendingScrollPkeyRef.current;
		if (!pkey) return;
		if (!products.some((p) => p.PKEY === pkey)) return;
		const t = setTimeout(() => {
			flashCard(pkey);
			pendingScrollPkeyRef.current = null;
		}, 120);
		return () => clearTimeout(t);
	}, [products, flashCard]);

	// Format number with 2 decimal places for GP
	const formatGPPercentage = (num) => {
		const value = Number(num);
		// If value is very small but not zero, show as 0.01 to avoid showing 0
		if (value > 0 && value < 0.01) {
			return 0.01;
		}
		return Math.round(value * 100) / 100;
	};

	// Format number with 2 decimal places for GP value
	const formatGPValue = (num) => {
		const value = Number(num);
		// If value is very small but not zero, round to 1 to avoid showing 0
		if (value > 0 && value < 1) {
			return 1;
		}
		return Math.round(value).toLocaleString("en-IN");
	};

	// Helper function for client-side sorting
	const sortProducts = (products, sortField, sortDirection) => {
		if (products.length === 0) {
			return products;
		}

		return [...products].sort((a, b) => {
			let aValue, bValue;

			// Map field names to actual data properties
			switch (sortField) {
				case "period_st":
					aValue = getProductPeriodStPct(a);
					bValue = getProductPeriodStPct(b);
					break;
				case "st":
					aValue = parseFloat(a.troco_ls?.st) || 0;
					bValue = parseFloat(b.troco_ls?.st) || 0;
					break;
				case "qty":
				case "sold_in_period":
					aValue = parseFloat(a.troco_ls?.t_sale) || 0;
					bValue = parseFloat(b.troco_ls?.t_sale) || 0;
					break;
				case "total_sold":
					aValue = parseFloat(a.troco_ls?.t_sale_total) || 0;
					bValue = parseFloat(b.troco_ls?.t_sale_total) || 0;
					break;
				case "inw_qty":
					// Inward / T.INW quantity
					aValue = parseFloat(String(a.troco_ls?.t_inw ?? "").replace(/,/g, "")) || 0;
					bValue = parseFloat(String(b.troco_ls?.t_inw ?? "").replace(/,/g, "")) || 0;
					break;
				case "cogs":
					aValue = parseProductCogs(a);
					bValue = parseProductCogs(b);
					break;
				case "gp_cont":
					// GP Contribution: gp_value
					aValue = parseFloat(a.lcogs?.gp_value) || 0;
					bValue = parseFloat(b.lcogs?.gp_value) || 0;
					break;
				case "period_ros":
					aValue = parseFloat(a.period_ros) || 0;
					bValue = parseFloat(b.period_ros) || 0;
					break;
				case "ros":
					aValue = parseFloat(a.rate_of_sale) || 0;
					bValue = parseFloat(b.rate_of_sale) || 0;
					break;
				case "doh":
					// Days on Hands - calculate average DOH
					const aDohValues = [a.ebo_doh || 0, a.lfs_doh || 0, a.mbo_doh || 0, a.ecom_doh || 0].filter((value) => value > 0);
					const bDohValues = [b.ebo_doh || 0, b.lfs_doh || 0, b.mbo_doh || 0, b.ecom_doh || 0].filter((value) => value > 0);

					aValue =
						aDohValues.length > 0 ? Math.round(aDohValues.reduce((sum, value) => sum + value, 0) / aDohValues.length) : 0;
					bValue =
						bDohValues.length > 0 ? Math.round(bDohValues.reduce((sum, value) => sum + value, 0) / bDohValues.length) : 0;
					break;
				case "aging": {
					const agingVal = (p) => {
						if (p.avg_aging != null && p.avg_aging !== "" && !Number.isNaN(Number(p.avg_aging))) {
							return Number(p.avg_aging);
						}
						const vals = [p.ebo_aging || 0, p.lfs_aging || 0, p.mbo_aging || 0, p.ecom_aging || 0].filter((v) => v > 0);
						return vals.length > 0 ? Math.round(vals.reduce((s, v) => s + v, 0) / vals.length) : 0;
					};
					aValue = agingVal(a);
					bValue = agingVal(b);
					break;
				}
				case "max_recv_aging":
				case "last_aging": {
					const lastAgingVal = (p) => {
						if (p.avg_max_aging != null && p.avg_max_aging !== "" && !Number.isNaN(Number(p.avg_max_aging))) {
							return Number(p.avg_max_aging);
						}
						const vals = [p.ebo_max_aging || 0, p.lfs_max_aging || 0, p.mbo_max_aging || 0, p.ecom_max_aging || 0].filter(
							(v) => v > 0
						);
						return vals.length > 0 ? Math.round(vals.reduce((s, v) => s + v, 0) / vals.length) : 0;
					};
					aValue = lastAgingVal(a);
					bValue = lastAgingVal(b);
					break;
				}
				case "multi_mrp_cogs": {
					const mult = (p) => {
						const mrp = parseFloat(String(p.troco_ls?.mrp ?? "").replace(/,/g, "")) || 0;
						const cogs = parseFloat(String(p.COGS ?? "").replace(/,/g, "")) || 0;
						if (!cogs) return 0;
						return mrp / cogs;
					};
					aValue = mult(a);
					bValue = mult(b);
					break;
				}
				default:
					return 0;
			}

			if (sortDirection === "asc") {
				return aValue - bValue;
			} else {
				return bValue - aValue;
			}
		});
	};

	const handleSort = (field) => {
		setPagination((prev) => ({ ...prev, page: 1, isPageChange: false }));

		// Update sort config
		const newSortConfig = {
			field: field,
			direction: sortConfig.field === field && sortConfig.direction === "desc" ? "asc" : "desc",
		};
		setSortConfig(newSortConfig);

		// Client-side sorting using cached data
		const sortedProducts = sortProducts(allProducts, field, newSortConfig.direction);

		setAllProducts(sortedProducts);
	};

	/** Up = desc (high → low); down = asc (low → high). Inactive shows up (first click sorts desc). */
	const renderSortButton = (field, label) => {
		const isActive = sortConfig.field === field;
		const isHovered = hoveredSort === field;
		const arrow =
			isActive && sortConfig.direction === "desc" ? (
				<KeyboardArrowUpIcon fontSize="small" />
			) : isActive && sortConfig.direction === "asc" ? (
				<KeyboardArrowDownIcon fontSize="small" />
			) : (
				<KeyboardArrowUpIcon fontSize="small" />
			);

		return (
			<Button
				key={field}
				variant={isActive ? "contained" : "outlined"}
				onClick={() => handleSort(field)}
				size="small"
				onMouseEnter={() => setHoveredSort(field)}
				onMouseLeave={() => setHoveredSort(null)}
				sx={{ position: "relative" }}>
				{label}
				<span
					className="sort-arrow"
					style={{
						opacity: isActive || isHovered ? 1 : 0.3,
						transition: "opacity 0.2s ease",
						marginLeft: "4px",
						display: "inline-flex",
						alignItems: "center",
						pointerEvents: "none",
					}}>
					{arrow}
				</span>
			</Button>
		);
	};

	// Add live clock update
	useEffect(() => {
		const timer = setInterval(() => {
			setCurrentDateTime(new Date());
		}, 1000);

		return () => clearInterval(timer);
	}, []);

	const waitForImagesInElement = (root) => {
		if (!root) return Promise.resolve();
		const images = root.getElementsByTagName("img");
		if (images.length === 0) return Promise.resolve();
		return Promise.all(
			Array.from(images).map(
				(img) =>
					new Promise((resolve) => {
						if (img.complete) resolve();
						else {
							img.onload = () => resolve();
							img.onerror = () => resolve();
						}
					})
			)
		);
	};

	const handleDownloadPDF = useCallback(
		async (downloadType = "all", tileFieldMask) => {
			if (pdfGenerating) {
				return;
			}

			const captureEl = pdfRef.current;
			if (!captureEl) {
				setError("Report view is not ready for PDF export.");
				return;
			}

			const mergedPdfFieldMask = { ...defaultExportFieldMask(), ...(tileFieldMask || {}) };
			if (!hasTileExportFieldsSelected(mergedPdfFieldMask)) {
				setError("Select at least one product tile field to include in the PDF.");
				return;
			}
			pdfExportFieldMaskRef.current = mergedPdfFieldMask;

			const perPage = pagination.per_page;
			const exportAllList = productsForAllPagesExport;
			const totalPdfPages =
				downloadType === "current" ? 1 : Math.max(1, Math.ceil(exportAllList.length / perPage));

			if (downloadType === "current") {
				if (!products || products.length === 0) {
					setError("No products available to download");
					return;
				}
			} else if (!exportAllList || exportAllList.length === 0) {
				setError("No products available to download");
				return;
			}

			const restorePage = pagination.page;
			let pdfStyleEl = null;
			let popupNotification = null;
			let blocker = null;

			const restoreListing = () => {
				if (downloadType !== "all") return;
				const listAfterExport = activeQuickFilter ? paginationSourceList : allProducts;
				const startIdx = (restorePage - 1) * perPage;
				flushSync(() => {
					setProducts(listAfterExport.slice(startIdx, startIdx + perPage));
					setPagination((prev) => ({ ...prev, page: restorePage }));
				});
			};

			try {
				setPdfGenerating(true);
				setPdfProgress(0);

				const pdf = new jsPDF({
					orientation: "landscape",
					unit: "mm",
					format: "a2",
				});

				blocker = document.createElement("div");
				blocker.style.cssText =
					"position:fixed;inset:0;background:rgba(255,255,255,0.92);z-index:9998;cursor:wait;";
				document.body.appendChild(blocker);

				popupNotification = document.createElement("div");
				popupNotification.style.cssText = `
					position: fixed;
					top: 20px;
					right: 20px;
					width: 300px;
					background: white;
					border-radius: 12px;
					box-shadow: 0 4px 20px rgba(0,0,0,0.15);
					z-index: 10000;
					font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif;
					border: 1px solid #e0e0e0;
					overflow: hidden;
					animation: slideInRight 0.3s ease-out;
				`;

				pdfStyleEl = document.createElement("style");
				pdfStyleEl.textContent = `
					@keyframes slideInRight {
						from { transform: translateX(100%); opacity: 0; }
						to { transform: translateX(0); opacity: 1; }
					}
				`;
				document.head.appendChild(pdfStyleEl);

				const progressContent = document.createElement("div");
				progressContent.style.cssText = `padding: 20px; text-align: center;`;
				popupNotification.appendChild(progressContent);

				const progressBarContainer = document.createElement("div");
				progressBarContainer.style.cssText = `
					width: 100%;
					height: 4px;
					background: #e0e0e0;
					border-radius: 2px;
					overflow: hidden;
					margin-top: 10px;
				`;
				popupNotification.appendChild(progressBarContainer);

				const progressBar = document.createElement("div");
				progressBar.style.cssText = `
					height: 100%;
					background: #000;
					width: 0%;
					transition: width 0.3s ease;
					border-radius: 2px;
				`;
				progressBarContainer.appendChild(progressBar);

				document.body.appendChild(popupNotification);

				const gridMarginMm = 14;
				const totalGridPages = totalPdfPages;
				const totalSteps = 1 + totalGridPages;
				let stepsDone = 0;

				const updatePdfProgress = (detail) => {
					stepsDone += 1;
					const progress = Math.min(100, Math.round((stepsDone / totalSteps) * 100));
					setPdfProgress(progress);
					progressContent.innerHTML = `
						<h3 style="margin: 0 0 10px 0; color: #333; font-size: 16px;">Generating PDF</h3>
						<p style="margin: 5px 0; color: #666; font-size: 14px;">${detail}</p>
						<p style="margin: 5px 0; color: #000; font-weight: 600; font-size: 14px;">${progress}% complete</p>
					`;
					progressBar.style.width = `${progress}%`;
				};

				const headerArgs = {
					currentDateTime,
					dateRange,
					totals,
					calculateST,
					calculatePeriodST,
					formatNumber,
					pageWidth: pdf.internal.pageSize.getWidth(),
					filters,
					quickFilterLabel: quickFilterExportLabel,
				};

				// Page 1: summary + applied filters only (no product grid)
				updatePdfProgress("Page 1: summary and applied filters");
				const coverContentEndY = addHeader(pdf, headerArgs);
				pdf.setFontSize(9);
				pdf.setTextColor(100, 100, 100);
				const noteY = Math.min(coverContentEndY + 8, pdf.internal.pageSize.getHeight() - 12);
				pdf.text("Product pictorial begins on the next page.", headerArgs.pageWidth / 2, noteY, {
					align: "center",
				});
				pdf.setTextColor(0, 0, 0);

				for (let gridPage = 1; gridPage <= totalGridPages; gridPage++) {
					updatePdfProgress(`Product grid: page ${gridPage} of ${totalGridPages}`);
					pdf.addPage();

					if (downloadType === "all") {
						const startIdx = (gridPage - 1) * perPage;
						const pageSlice = exportAllList.slice(startIdx, startIdx + perPage);
						flushSync(() => {
							setProducts(pageSlice);
							setPagination((prev) => ({ ...prev, page: gridPage }));
						});
					}

					await waitForImagesInElement(pdfRef.current);
					await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
					await new Promise((r) => setTimeout(r, 150));

					const liveRoot = pdfRef.current;
					const canvas = await html2canvas(liveRoot, {
						scale: 2,
						useCORS: true,
						allowTaint: true,
						backgroundColor: "#ffffff",
						logging: false,
						onclone: (clonedDoc) => {
							preparePdfDomClone(liveRoot, clonedDoc, pdfExportFieldMaskRef.current);
						},
					});

					const pageWidth = pdf.internal.pageSize.getWidth();
					const pageHeight = pdf.internal.pageSize.getHeight();
					const availW = pageWidth - 2 * gridMarginMm;
					const availH = pageHeight - 2 * gridMarginMm;
					const scale = Math.min(availW / canvas.width, availH / canvas.height);
					const scaledW = canvas.width * scale;
					const scaledH = canvas.height * scale;
					const x = (pageWidth - scaledW) / 2;
					const y = gridMarginMm + (availH - scaledH) / 2;
					pdf.addImage(canvas.toDataURL("image/png"), "PNG", x, y, scaledW, scaledH);
				}

				restoreListing();

				if (popupNotification?.parentNode) {
					document.body.removeChild(popupNotification);
				}
				if (blocker?.parentNode) {
					document.body.removeChild(blocker);
				}
				if (pdfStyleEl?.parentNode) {
					pdfStyleEl.remove();
				}

				const filterSlug = quickFilterExportLabel
					? `-${quickFilterExportLabel.replace(/[^a-zA-Z0-9]+/g, "-").replace(/^-|-$/g, "").toLowerCase()}`
					: "";
				const fileName =
					downloadType === "current"
						? `sell-through-report-page-${restorePage}${filterSlug}.pdf`
						: `sell-through-report-all-pages${filterSlug}.pdf`;

				pdf.save(fileName);
			} catch (error) {
				console.error("Error in PDF generation:", error);
				setError(`Failed to generate PDF: ${error.message}`);
				if (popupNotification?.parentNode) {
					document.body.removeChild(popupNotification);
				}
				if (blocker?.parentNode) {
					document.body.removeChild(blocker);
				}
				if (pdfStyleEl?.parentNode) {
					pdfStyleEl.remove();
				}
				restoreListing();
			} finally {
				setPdfGenerating(false);
				setPdfProgress(0);
			}
		},
		[
			pdfGenerating,
			pagination.page,
			pagination.per_page,
			products,
			allProducts,
			productsForAllPagesExport,
			paginationSourceList,
			activeQuickFilter,
			quickFilterExportLabel,
			dateRange,
			totals,
			calculateST,
			calculatePeriodST,
			formatNumber,
			currentDateTime,
			filters,
		]
	);

	// Helper function to add header to PDF
	const filterLabels = {
		brand: "Brand",
		subBrand: "Sub Brand",
		department: "Department",
		subCategory: "Sub Category",
		style: "Style",
		color: "Color",
		sleeve: "Sleeve",
		division: "Division",
		season: "Season",
		fashionability: "Fashionability",
		occasion: "Occasion",
		palette: "Palette",
		fitType: "Fit Type",
		fabricBlends: "Fabric Blend",
		nod: "NOD",
		channel: "Channel",
		store: "Store",
		city: "City",
		state: "State",
		region: "Region",
		tier: "Tier",
	};

	const addHeader = (pdf, { currentDateTime, dateRange, totals, calculateST, calculatePeriodST, formatNumber, pageWidth, filters, quickFilterLabel }) => {
		// Add black rectangle at the top
		pdf.setFillColor(0, 0, 0);
		pdf.rect(0, 0, pageWidth, 12, "F");

		// Add title in white
		pdf.setTextColor(255, 255, 255);
		pdf.setFont("helvetica", "bold");
		pdf.setFontSize(20);
		pdf.text("SELL THROUGH PICTORIAL", pageWidth / 2, 8, {
			align: "center",
			renderingMode: "fill",
		});

		// Reset text color to black for rest of the content
		pdf.setTextColor(0, 0, 0);
		pdf.setFontSize(10);
		pdf.setFont("helvetica", "normal");

		// Center-aligned header content
		const centerX = pageWidth / 2;
		const startY = 18;
		const lineHeight = 5;

		// Date and period info - center aligned
		pdf.text(`Date: ${format(currentDateTime, "dd-MM-yyyy HH:mm:ss")}`, centerX, startY, { align: "center" });
		pdf.text(
			`Period: ${format(dateRange.startDate, "dd-MM-yyyy")} - ${format(dateRange.endDate, "dd-MM-yyyy")}`,
			centerX,
			startY + lineHeight,
			{ align: "center" }
		);
		pdf.text(
			`Sub Category: ${totals.subCategory === "All" ? "All Sub Categories" : totals.subCategory}`,
			centerX,
			startY + lineHeight * 2,
			{ align: "center" }
		);

		// Summary stats - center aligned in a compact format
		const statsY = startY + lineHeight * 4;
		pdf.setFont(undefined, "bold");
		pdf.text(
			`Total Buy: ${formatNumber(totals.totalBuy)} | Sold: ${formatNumber(totals.totalSold)} | Period ST%: ${calculatePeriodST()}% | Total ST%: ${calculateST()}%`,
			centerX,
			statsY,
			{ align: "center" }
		);
		pdf.text(
			`MRP Value: ${formatNumber(totals.mrpValue)} | GP%: ${formatGPPercentage(
				totals.gpPercentage
			)}% | GP Value: ${formatGPValue(totals.gpValue)}`,
			centerX,
			statsY + lineHeight,
			{ align: "center" }
		);
		pdf.setFont(undefined, "normal");

		// Print selected filters - center aligned
		const filterEntries = Object.entries(filters)
			.filter(([key, value]) => filterLabels[key] && value && value !== "")
			.map(([key, value]) => `${filterLabels[key]}: ${value}`);

		// Applied filters — extra gap below KPI block so it reads as a separate section on the cover page
		let filterY = statsY + lineHeight * 4;
		pdf.setFont(undefined, "bold");
		pdf.text("Applied Filters:", centerX, filterY, { align: "center" });
		pdf.setFont(undefined, "normal");
		filterY += lineHeight;

		if (filterEntries.length > 0) {
			const filtersPerLine = 4;
			for (let i = 0; i < filterEntries.length; i += filtersPerLine) {
				const line = filterEntries.slice(i, i + filtersPerLine).join("    |    ");
				pdf.text(line, centerX, filterY, { align: "center" });
				filterY += lineHeight;
			}
		} else {
			pdf.text("No filters applied", centerX, filterY, { align: "center" });
			filterY += lineHeight;
		}

		if (quickFilterLabel) {
			filterY += lineHeight * 0.5;
			pdf.setFont(undefined, "bold");
			pdf.text("Stock quick filter:", centerX, filterY, { align: "center" });
			pdf.setFont(undefined, "normal");
			filterY += lineHeight;
			pdf.text(quickFilterLabel, centerX, filterY, { align: "center" });
			filterY += lineHeight;
		}

		// Y (mm) just below last line — for optional cover-page footnotes
		return filterY + 6;
	};

	// Add a function to handle applying filters
	const applyFilters = () => {
		setOpenDropdowns(new Set());
		setLoading(true);
		setPagination((prev) => ({ ...prev, page: 1, isPageChange: false }));
		setError("");

		try {
			isApplyingFiltersRef.current = true;
			const formattedStartDate = format(pendingDateRange.startDate, "yyyy-MM-dd");
			const formattedEndDate = format(pendingDateRange.endDate, "yyyy-MM-dd");
			const newFilters = {
				start_date: formattedStartDate,
				end_date: formattedEndDate,
			};

			Object.entries(selectedItems).forEach(([field, items]) => {
				const apiKey = field.endsWith("s") ? field.slice(0, -1) : field;
				if (["subCategories", "cities", "fashionability"].includes(field)) {
					const specialKey = field === "subCategories" ? "subCategory" : field === "cities" ? "city" : "fashionability";
					if (items.size > 0) {
						newFilters[specialKey] = Array.from(items).join(",");
					} else {
						delete newFilters[specialKey];
					}
				} else {
					if (items.size > 0) {
						newFilters[apiKey] = Array.from(items).join(",");
					} else {
						delete newFilters[apiKey];
					}
				}
			});
			setFilters(newFilters);
			setDateRange(pendingDateRange);

			// Trigger dependency filtering for the new filters
			if (filterOptionsLoadedRef.current) {
				// Check if any filters are active and rollback accordingly
				const hasActiveFilters = Object.entries(newFilters).some(
					([key, value]) =>
						value &&
						value.trim() !== "" &&
						key !== "start_date" &&
						key !== "end_date" &&
						key !== "sort_field" &&
						key !== "sort_direction"
				);

				if (!hasActiveFilters) {
					setFilterOptions(masterFilterOptions);
				}
			}

			// Products and company totals will be fetched automatically by useEffect when dateRange changes
		} catch (err) {
			console.error("Error applying filters:", err);
			setError("Error applying filters");
		} finally {
			// Reset immediately without delay
			isApplyingFiltersRef.current = false;
		}
	};

	// Add reset function
	const handleResetFilters = () => {
		// Reset search terms
		setSearchTerms({
			brand: "",
			subBrand: "",
			department: "",
			subCategory: "",
			style: "",
			color: "",
			sleeve: "",
			division: "",
			season: "",
			fashionability: "",
			occasion: "",
			palette: "",
			fitType: "",
			fabricBlend: "",
			nod: "",
			channel: "",
			store: "",
			city: "",
			state: "",
			region: "",
			tier: "",
		});

		// Reset pending filters
		setPendingFilters({
			department: "",
			subCategory: "",
			brand: "",
			subBrand: "",
			style: "",
			color: "",
			sleeve: "",
			division: "",
			season: "",
			fashionability: "",
			occasion: "",
			palette: "",
			fitType: "",
			fabricBlend: "",
			nod: "",
			channel: "",
			store: "",
			city: "",
			state: "",
			region: "",
			tier: "",
		});

		// Reset selected items
		setSelectedItems({
			brands: new Set(),
			subBrands: new Set(),
			departments: new Set(),
			subCategories: new Set(),
			styles: new Set(),
			colors: new Set(),
			sleeves: new Set(),
			divisions: new Set(),
			seasons: new Set(),
			fashionability: new Set(),
			occasions: new Set(),
			palettes: new Set(),
			fitTypes: new Set(),
			fabricBlends: new Set(),
			nods: new Set(),
			channels: new Set(),
			stores: new Set(),
			cities: new Set(),
			states: new Set(),
			regions: new Set(),
			tiers: new Set(),
		});

		// Reset filters but keep the date range
		const { start_date, end_date } = filters;
		setFilters({
			department: "",
			subCategory: "",
			brand: "",
			subBrand: "",
			style: "",
			color: "",
			sleeve: "",
			division: "",
			season: "",
			fashionability: "",
			occasion: "",
			palette: "",
			fitType: "",
			fabricBlend: "",
			nod: "",
			channel: "",
			store: "",
			city: "",
			state: "",
			region: "",
			tier: "",
			start_date,
			end_date,
		});

		// Reset pagination to first page
		setPagination((prev) => ({ ...prev, page: 1 }));

		// Reset filter options to master options (all available options)
		setFilterOptions(masterFilterOptions);

		// Clear products and error
		setProducts([]);
		setAllProducts([]);
		setRampColorDrilldown(null);
		setError("");
	};

	const loadingOverlayMessage =
		dataLoadPhase === "fetching"
			? "Fetching data..."
			: dataLoadPhase === "rendering"
				? "Rendering products..."
				: "Loading...";

	// Loading Indicator with transparent background
	const LoadingOverlay = () => (
		<Box
			sx={{
				position: "fixed",
				top: 0,
				left: 0,
				right: 0,
				bottom: 0,
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				backgroundColor: "rgba(255, 255, 255, 0.7)",
				zIndex: 1300,
				backdropFilter: "blur(3px)",
			}}>
			<Box
				sx={{
					display: "flex",
					flexDirection: "column",
					alignItems: "center",
					gap: 2,
					maxWidth: 320,
					px: 2,
					textAlign: "center",
				}}>
				<CircularProgress variant="indeterminate" sx={{ color: "#000" }} size={60} />
				<Typography sx={{ color: "#000", fontWeight: 500 }}>{loadingOverlayMessage}</Typography>
			</Box>
		</Box>
	);

	const escapeCsvCell = (value) => {
		const str = value == null ? "" : String(value);
		return `"${str.replace(/"/g, '""')}"`;
	};

	const handleDownloadCSV = useCallback(
		async (downloadType = "all", fieldMask) => {
			try {
				setLoading(true);

				const mergedMask = { ...defaultExportFieldMask(), ...(fieldMask || {}) };
				if (!hasTileExportFieldsSelected(mergedMask)) {
					throw new Error("Select at least one product field to include in the CSV.");
				}

				const productsToProcess =
					downloadType === "current" ? products : productsForAllPagesExport;
				const filterSlug = quickFilterExportLabel
					? `-${quickFilterExportLabel.replace(/[^a-zA-Z0-9]+/g, "-").replace(/^-|-$/g, "").toLowerCase()}`
					: "";
				const fileName =
					downloadType === "current"
						? `sell-through-report-page-${pagination.page}${filterSlug}.csv`
						: `sell-through-report-all-pages${filterSlug}.csv`;

				if (!productsToProcess || productsToProcess.length === 0) {
					throw new Error("No data available to download");
				}

				const csvRows = [];
				if (mergedMask.summary_header) {
					csvRows.push(
						[
							escapeCsvCell("Report"),
							escapeCsvCell("Sell Through Pictorial"),
						].join(","),
						...(quickFilterExportLabel
							? [
									[
										escapeCsvCell("Stock quick filter"),
										escapeCsvCell(quickFilterExportLabel),
									].join(","),
								]
							: []),
						[
							escapeCsvCell("Period"),
							escapeCsvCell(
								`${format(dateRange.startDate, "yyyy-MM-dd")} to ${format(dateRange.endDate, "yyyy-MM-dd")}`
							),
						].join(","),
						[
							escapeCsvCell("Total Buy Pcs"),
							escapeCsvCell(totals.totalBuy),
						].join(","),
						[
							escapeCsvCell("Total Sold Pcs (period)"),
							escapeCsvCell(totals.totalSold),
						].join(","),
						[
							escapeCsvCell("Sold Period ST%"),
							escapeCsvCell(`${calculatePeriodST()}%`),
						].join(","),
						[
							escapeCsvCell("Total ST%"),
							escapeCsvCell(`${calculateST()}%`),
						].join(","),
						[
							escapeCsvCell("Buy MRP Value"),
							escapeCsvCell(totals.mrpValue),
						].join(","),
						[
							escapeCsvCell("Sold MRP Value"),
							escapeCsvCell(totals.soldMRPValue),
						].join(","),
						""
					);
				}

				const selectedCols = TILE_EXPORT_FIELD_IDS.filter((id) => mergedMask[id] && EXPORT_CSV_FIELD_MAP[id]).map(
					(id) => ({ id, ...EXPORT_CSV_FIELD_MAP[id] })
				);

				csvRows.push(selectedCols.map((c) => escapeCsvCell(c.header)).join(","));
				productsToProcess.forEach((product) => {
					csvRows.push(
						selectedCols.map((c) => escapeCsvCell(c.getValue(product))).join(",")
					);
				});

				const csvContent = csvRows.join("\n");
				const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
				const link = document.createElement("a");
				const url = URL.createObjectURL(blob);
				link.setAttribute("href", url);
				link.setAttribute("download", fileName);
				link.style.visibility = "hidden";
				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);
				URL.revokeObjectURL(url);
			} catch (error) {
				console.error("Error in CSV generation:", error);
				setError(`Failed to generate CSV: ${error.message}`);
			} finally {
				setLoading(false);
			}
		},
		[products, productsForAllPagesExport, quickFilterExportLabel, pagination.page, dateRange, totals, calculateST, calculatePeriodST]
	);

	// Download dropdown handlers
	const handleCurrentPageDownloadClick = (event) => {
		setCurrentPageDownloadAnchor(event.currentTarget);
		setCurrentPageDownloadOpen(!currentPageDownloadOpen);
	};

	const handleAllPagesDownloadClick = (event) => {
		setAllPagesDownloadAnchor(event.currentTarget);
		setAllPagesDownloadOpen(!allPagesDownloadOpen);
	};

	const handleDownloadMenuClose = () => {
		setCurrentPageDownloadAnchor(null);
		setAllPagesDownloadAnchor(null);
		setCurrentPageDownloadOpen(false);
		setAllPagesDownloadOpen(false);
	};

	const openExportFieldPicker = (scope, format = "pdf") => {
		setPdfFieldPickerScope(scope);
		setExportPickerFormat(format);
		setPdfFieldPickerOpen(true);
		handleDownloadMenuClose();
	};

	const handlePdfFieldToggle = (id) => {
		setPdfFieldSelection((prev) => ({ ...prev, [id]: !prev[id] }));
	};

	const handlePdfFieldSelectAll = () => {
		setPdfFieldSelection(defaultPdfTileFieldMask());
	};

	const handlePdfFieldClearAll = () => {
		setPdfFieldSelection(Object.fromEntries(PICTORIAL_EXPORT_FIELDS.map(({ id }) => [id, false])));
	};

	const handlePdfFieldPickerCancel = () => {
		setPdfFieldPickerOpen(false);
	};

	const handlePdfFieldPickerExport = () => {
		if (!hasTileExportFieldsSelected(pdfFieldSelection)) {
			setError("Select at least one product field to include in the export.");
			return;
		}
		setError("");
		setPdfFieldPickerOpen(false);
		const mask = { ...pdfFieldSelection };
		if (exportPickerFormat === "csv") {
			handleDownloadCSV(pdfFieldPickerScope, mask);
		} else {
			handleDownloadPDF(pdfFieldPickerScope, mask);
		}
	};

	// Navigation functions
	const openUploadModal = () => setUploadModalOpen(true);
	const closeUploadModal = () => setUploadModalOpen(false);

	// Function to handle applying filters from upload page
	const handleApplyFiltersFromUpload = (filters, dateRange) => {
		try {
			// Set the date range
			setDateRange(dateRange);
			setPendingDateRange(dateRange);

			// Convert filter values to selectedItems format
			const newSelectedItems = { ...selectedItems };
			const newFilters = {
				start_date: format(dateRange.startDate, "yyyy-MM-dd"),
				end_date: format(dateRange.endDate, "yyyy-MM-dd"),
			};

			// Map filter values to selectedItems and filters
			Object.entries(filters).forEach(([key, value]) => {
				if (key === "start_date" || key === "end_date") return; // Skip date fields

				const values = value
					.split(",")
					.map((v) => v.trim())
					.filter((v) => v);
				if (values.length > 0) {
					// Map filter key to selectedItems key
					let selectedKey;
					switch (key) {
						case "subCategory":
							selectedKey = "subCategories";
							break;
						case "city":
							selectedKey = "cities";
							break;
						case "palette":
							selectedKey = "palettes";
							break;
						case "fashionability":
							selectedKey = "fashionability";
							break;
						default:
							selectedKey = `${key}s`;
					}

					newSelectedItems[selectedKey] = new Set(values);

					// Also update the filters state immediately
					const apiKey = key;
					newFilters[apiKey] = values.join(",");
				}
			});

			// Set both selectedItems and filters
			setSelectedItems(newSelectedItems);
			setFilters(newFilters);

			// Don't auto-apply filters - let user make manual selections first
			// User will need to click the search button to apply all filters
		} catch (error) {
			console.error("Error applying filters from upload:", error);
		}
	};

	// Warn on page/tab close during PDF generation
	useEffect(() => {
		if (pdfGenerating) {
			window.onbeforeunload = (e) => {
				e.preventDefault();
				e.returnValue = "A PDF is still being generated. Are you sure you want to leave this page?";
				return e.returnValue;
			};
		} else {
			window.onbeforeunload = null;
		}
	}, [pdfGenerating]);

	// Derived header metrics (for snapshot alignment)
	const buyMrpContValue = totals.mrpValue || 0;
	const buyMrpContPct = companyTotals.mrpBuyValue > 0 ? (buyMrpContValue / companyTotals.mrpBuyValue) * 100 : 0;

	const saleContCatValue = totals.soldMRPValue || 0; // using sold MRP value as contribution value
	const saleContCatPct = companyTotals.mrpSoldValue > 0 ? (saleContCatValue / companyTotals.mrpSoldValue) * 100 : 0;

	const discountPctAvg = (() => {
		const values = allProducts.map((p) => parseFloat(p.discount_percentage || 0)).filter((v) => !isNaN(v) && v >= 0);
		if (values.length === 0) return 0;
		return values.reduce((a, b) => a + b, 0) / values.length;
	})();

	const channelSoldSplit = useMemo(() => {
		const rows = PICTORIAL_CHANNEL_SOLD_DEF.map(({ label, periodKey, overallKey }) => ({
			label,
			period: allProducts.reduce((sum, product) => sum + (Number(product[periodKey]) || 0), 0),
			overall: allProducts.reduce((sum, product) => sum + (Number(product[overallKey]) || 0), 0),
		})).filter((row) => row.period > 0 || row.overall > 0);

		const periodTotal = rows.reduce((sum, row) => sum + row.period, 0);
		const overallTotal = rows.reduce((sum, row) => sum + row.overall, 0);

		return {
			rows: rows.map((row) => ({
				...row,
				periodPct: channelSoldContributionPct(row.period, periodTotal),
				overallPct: channelSoldContributionPct(row.overall, overallTotal),
			})),
			periodTotal,
			overallTotal,
		};
	}, [allProducts]);

	/** GP Cont/Cat Value in header — same scope/rounding as the contribution breakdown. */
	const displayedGpContCatValue = useMemo(() => {
		const sumCategoryGp = (categories) => categories.reduce((sum, cat) => sum + cat.gpValue, 0);

		if (gpContributionCard.open) {
			if (gpContributionCard.viewMode === "categories" && gpContributionCard.categoryContributions.length > 0) {
				return sumCategoryGp(gpContributionCard.categoryContributions);
			}
			if (gpContributionCard.viewMode === "brands" && gpContributionCard.brandContributions.length > 0) {
				return gpContributionCard.brandContributions.reduce((sum, brand) => sum + brand.gpValue, 0);
			}
			if (gpContributionCard.viewMode === "colors") {
				const rows =
					gpContributionCard.colorContributions.length > 0
						? gpContributionCard.colorContributions
						: gpContributionCard.colorContributionsTinw;
				if (rows.length > 0) {
					return rows.reduce((sum, row) => sum + (row.gpValue || 0), 0);
				}
			}
		}

		const selectedBrands = selectedItems.brands;
		if (selectedBrands?.size === 1) {
			const brand = Array.from(selectedBrands)[0];
			return sumGpValueForProducts(
				allProducts.filter((p) => (p.brand || "Unknown").toLowerCase() === brand.toLowerCase())
			);
		}

		return totals.gpValue;
	}, [gpContributionCard, totals.gpValue, selectedItems.brands, allProducts]);

	return (
		<ThemeProvider theme={theme}>
			<Container maxWidth={false} sx={{ pt: 1, px: { xs: 2, sm: 3, md: 4, lg: 6, xl: 8 } }}>
				{filterOptionsLoading ? (
					<Box
						sx={{
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
							minHeight: "80vh",
							flexDirection: "column",
							gap: 2,
						}}>
						<CircularProgress
							variant={queryProgress > 0 ? "determinate" : "indeterminate"}
							value={queryProgress}
							size={60}
							sx={{ color: "#000" }}
						/>
						<Typography variant="h6" sx={{ color: "#000" }}>
							{queryProgress > 0 ? `Loading data... ${queryProgress}%` : "Loading data..."}
						</Typography>
					</Box>
				) : (
					<Paper sx={{ p: { xs: 2, sm: 3, md: 4 }, mb: 4, backgroundColor: "#fff" }}>
						{/* Sticky Header Action Bar */}
						<Box
							sx={{
								position: "sticky",
								top: 0,
								zIndex: 10,
								backgroundColor: "rgba(255, 255, 255, 0.9)",
								backdropFilter: "blur(8px)",
								pb: 2,
								pt: 1,
								borderBottom: "1px solid #f0f0f0",
								mb: 3,
								display: "flex",
								flexDirection: { xs: "column", xl: "row" },
								alignItems: { xs: "stretch", xl: "center" },
								justifyContent: "space-between",
								gap: 2,
								margin: "-24px -32px 32px -32px",
								padding: "24px 32px 16px 32px",
							}}>
							<Box sx={{ display: "flex", flexDirection: "column" }}>
								<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
									<IconButton
										onClick={() => setFiltersExpanded(!filtersExpanded)}
										size="small"
										sx={{ mr: 0.5, ml: -1, color: "#000" }}>
										{filtersExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
									</IconButton>
									<Typography variant="h5" sx={{ color: "#000", fontWeight: 600, m: 0 }}>
										SELL THROUGH PICTORIAL
									</Typography>
								</Box>
								<Typography variant="caption" sx={{ color: "#666", mt: 0.5, fontWeight: 500 }}>
									{format(currentDateTime, "dd MMM yyyy HH:mm")} • {format(dateRange.startDate, "dd MMM yyyy")} - {format(dateRange.endDate, "dd MMM yyyy")} • {totals.subCategory === "All" ? "All Sub Categories" : totals.subCategory}
								</Typography>
								{/* Active Filter Chips */}
								{hasActiveFilters() && (
									<Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 1 }}>
										{Object.entries(filters).map(([k, v]) => {
											if (!v || ["start_date", "end_date"].includes(k)) return null;
											return (
												<Chip
													key={k}
													label={`${k.replace(/([A-Z])/g, ' $1').trim().toUpperCase()}: ${v}`}
													size="small"
													onDelete={() => {
														const newFilters = { ...filters, [k]: "" };
														setFilters(newFilters);
														setPendingFilters(newFilters);
														const mappedKey = filterKeyMap[k] || `${k}s`;
														setSelectedItems((prev) => ({ ...prev, [mappedKey]: new Set() }));
														if (filterOptionsLoadedRef.current) {
															const hasRemainingFilters = Object.entries(newFilters).some(
																([key, value]) =>
																	value &&
																	String(value).trim() !== "" &&
																	!["start_date", "end_date", "sort_field", "sort_direction"].includes(key)
															);
															if (!hasRemainingFilters) {
																setFilterOptions(masterFilterOptions);
															} else {
																scheduleFilterDependencies(newFilters);
															}
														}
													}}
													sx={{ fontSize: "0.65rem", height: 20, backgroundColor: "rgba(0,0,0,0.04)" }}
												/>
											);
										})}
									</Box>
								)}
							</Box>

							{/* Global Actions */}
							<Stack direction="row" spacing={1} sx={{ flexShrink: 0, flexWrap: "wrap", gap: 1 }}>
								<Button
									variant="outlined"
									onClick={openUploadModal}
									size="small"
									startIcon={<UploadFileIcon />}
									sx={{
										borderColor: "#28a745",
										color: "#28a745",
										"&:hover": {
											borderColor: "#218838",
											backgroundColor: "rgba(40, 167, 69, 0.04)",
										},
									}}>
									CSV Upload
								</Button>
								<Tooltip
									title={<PictorialStPdfExportTooltipTitle />}
									arrow
									placement="left"
									slotProps={{
										tooltip: {
											sx: {
												maxWidth: 420,
												bgcolor: "grey.900",
												"& .MuiTypography-root": { color: "#fff" },
											},
										},
									}}>
									<IconButton
										size="small"
										aria-label="What metrics and attributes are included in PDF export"
										sx={{
											color: "#666",
											border: "1px solid #e0e0e0",
											borderRadius: 1,
											"&:hover": { bgcolor: "action.hover" },
										}}>
										<HelpOutlineIcon fontSize="small" />
									</IconButton>
								</Tooltip>
								<Button
									variant="outlined"
									onClick={handleCurrentPageDownloadClick}
									disabled={loading}
									size="small"
									endIcon={currentPageDownloadOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}>
									Download Current Page
								</Button>
								<Menu
									anchorEl={currentPageDownloadAnchor}
									open={Boolean(currentPageDownloadAnchor)}
									onClose={handleDownloadMenuClose}
									anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
									transformOrigin={{ vertical: "top", horizontal: "left" }}>
									<MenuItem onClick={() => openExportFieldPicker("current", "pdf")} sx={{ alignItems: "flex-start", maxWidth: 340, py: 1.5 }}>
										<ListItemText
											primary="As PDF"
											secondary={pdfExportMenuSecondary}
											secondaryTypographyProps={{ variant: "caption", sx: { whiteSpace: "normal", display: "block", mt: 0.5, lineHeight: 1.4 } }}
										/>
									</MenuItem>
									<MenuItem onClick={() => openExportFieldPicker("current", "csv")} sx={{ alignItems: "flex-start", maxWidth: 340, py: 1.5 }}>
										<ListItemText
											primary="As CSV"
											secondary="Choose fields (same picker as PDF)."
											secondaryTypographyProps={{ variant: "caption", sx: { whiteSpace: "normal", display: "block", mt: 0.5, lineHeight: 1.4 } }}
										/>
									</MenuItem>
								</Menu>
								<Button
									variant="contained"
									onClick={handleAllPagesDownloadClick}
									disabled={loading}
									size="small"
									endIcon={allPagesDownloadOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}>
									Download All Pages
								</Button>
								<Menu
									anchorEl={allPagesDownloadAnchor}
									open={Boolean(allPagesDownloadAnchor)}
									onClose={handleDownloadMenuClose}
									anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
									transformOrigin={{ vertical: "top", horizontal: "left" }}>
									<MenuItem onClick={() => openExportFieldPicker("all", "pdf")} sx={{ alignItems: "flex-start", maxWidth: 340, py: 1.5 }}>
										<ListItemText
											primary="As PDF"
											secondary={pdfExportMenuSecondary}
											secondaryTypographyProps={{ variant: "caption", sx: { whiteSpace: "normal", display: "block", mt: 0.5, lineHeight: 1.4 } }}
										/>
									</MenuItem>
									<MenuItem onClick={() => openExportFieldPicker("all", "csv")} sx={{ alignItems: "flex-start", maxWidth: 340, py: 1.5 }}>
										<ListItemText
											primary="As CSV"
											secondary="Choose fields (same picker as PDF)."
											secondaryTypographyProps={{ variant: "caption", sx: { whiteSpace: "normal", display: "block", mt: 0.5, lineHeight: 1.4 } }}
										/>
									</MenuItem>
								</Menu>
							</Stack>
						</Box>

							{/* Summary stats — full width so five cards are not clipped */}
							<Grid container spacing={2} sx={{ mb: 3 }}>
							<Grid item xs={12} sx={{ minWidth: 0 }}>
								<Box sx={PICTORIAL_SUMMARY_STRIP_GRID_SX}>
									{/* Channel-wise sold pcs */}
									<Box sx={PICTORIAL_SUMMARY_BOX_SX}>
										<Typography variant="body1" sx={PICTORIAL_SUMMARY_HEADER_SX}>
											SOLD BY CHANNEL
										</Typography>
										<Box sx={{ ...PICTORIAL_CHANNEL_SOLD_GRID_SX, mb: 0.5, pb: 0.5, borderBottom: "1px solid #eee" }}>
											<Box component="span" />
											<Box component="span" sx={{ ...PICTORIAL_CHANNEL_SOLD_VALUE_SX, fontWeight: "bold" }}>
												Period
												<Box component="span" sx={PICTORIAL_SUMMARY_SUBVALUE_SX}>pcs (%)</Box>
											</Box>
											<Box component="span" sx={{ ...PICTORIAL_CHANNEL_SOLD_VALUE_SX, fontWeight: "bold" }}>
												Overall
												<Box component="span" sx={PICTORIAL_SUMMARY_SUBVALUE_SX}>pcs (%)</Box>
											</Box>
										</Box>
										{channelSoldSplit.rows.length === 0 ? (
											<Typography variant="body2" sx={{ fontSize: "0.625rem", color: "#666", textAlign: "center" }}>
												No channel sales in view
											</Typography>
										) : (
											channelSoldSplit.rows.map((row) => (
												<Box key={row.label} sx={PICTORIAL_CHANNEL_SOLD_GRID_SX}>
													<Box component="span" sx={{ fontWeight: "bold" }}>
														{row.label}:
													</Box>
													<Box component="span" sx={PICTORIAL_CHANNEL_SOLD_VALUE_SX}>
														{formatNumber(row.period)}
														<Box component="span" sx={PICTORIAL_SUMMARY_SUBVALUE_SX}>
															({row.periodPct}%)
														</Box>
													</Box>
													<Box component="span" sx={PICTORIAL_CHANNEL_SOLD_VALUE_SX}>
														{formatNumber(row.overall)}
														<Box component="span" sx={PICTORIAL_SUMMARY_SUBVALUE_SX}>
															({row.overallPct}%)
														</Box>
													</Box>
												</Box>
											))
										)}
										{channelSoldSplit.rows.length > 0 && (
											<Box
												sx={{
													...PICTORIAL_CHANNEL_SOLD_GRID_SX,
													mb: 0,
													pt: 0.5,
													borderTop: "1px solid #eee",
												}}>
												<Box component="span" sx={{ fontWeight: "bold" }}>Total:</Box>
												<Box component="span" sx={PICTORIAL_CHANNEL_SOLD_VALUE_SX}>
													{formatNumber(channelSoldSplit.periodTotal)}
													<Box component="span" sx={PICTORIAL_SUMMARY_SUBVALUE_SX}>
														({channelSoldSplit.periodTotal > 0 ? "100.0" : "0.0"}%)
													</Box>
												</Box>
												<Box component="span" sx={PICTORIAL_CHANNEL_SOLD_VALUE_SX}>
													{formatNumber(channelSoldSplit.overallTotal)}
													<Box component="span" sx={PICTORIAL_SUMMARY_SUBVALUE_SX}>
														({channelSoldSplit.overallTotal > 0 ? "100.0" : "0.0"}%)
													</Box>
												</Box>
											</Box>
										)}
									</Box>

									{/* Buy-based metrics */}
									<Box sx={PICTORIAL_SUMMARY_BOX_SX}>
										<Typography variant="body1" sx={PICTORIAL_SUMMARY_HEADER_SX}>
											BUY BASED
										</Typography>
										<Typography
											variant="body1"
											sx={PICTORIAL_SUMMARY_ROW_SX}>
											<Box component="span" sx={{ fontWeight: "bold" }}>
												Total Buy Pcs:
											</Box>
											<Box component="span">{formatNumber(totals.totalBuy)}</Box>
										</Typography>
										<Typography
											variant="body1"
											sx={PICTORIAL_SUMMARY_ROW_SX}>
											<Box component="span" sx={{ fontWeight: "bold" }}>
												MRP Value:
											</Box>
											<Box component="span">{formatNumber(totals.mrpValue)}</Box>
										</Typography>
										<Typography
											variant="body1"
											sx={PICTORIAL_SUMMARY_ROW_SX}>
											<Box component="span" sx={{ fontWeight: "bold" }}>
												Avg. MRP:
											</Box>
											<Box component="span">{formatNumber(totals.avgMRP)}</Box>
										</Typography>
										<Typography
											variant="body1"
											sx={PICTORIAL_SUMMARY_ROW_SX}>
											<Box component="span" sx={{ fontWeight: "bold" }}>
												COGS Value:
											</Box>
											<Box component="span">{formatNumber(totals.buyCogsValue || 0)}</Box>
										</Typography>
										<Typography
											variant="body1"
											sx={PICTORIAL_SUMMARY_ROW_SX}>
											<Box component="span" sx={{ fontWeight: "bold" }}>
												Avg. COGS:
											</Box>
											<Box component="span">{formatAvgRate(totals.buyAvgCOGS)}</Box>
										</Typography>
										<Typography
											variant="body1"
											sx={PICTORIAL_SUMMARY_ROW_SX}>
											<Box component="span" sx={{ fontWeight: "bold" }}>
												Multi:
											</Box>
											<Box component="span">{(totals.buyMulti || 0).toFixed(2)}</Box>
										</Typography>
									</Box>

									{/* Sold-based metrics */}
									<Box sx={PICTORIAL_SUMMARY_BOX_SX}>
										<Typography variant="body1" sx={PICTORIAL_SUMMARY_HEADER_SX}>
											SOLD BASED
										</Typography>
										<Typography
											variant="body1"
											sx={PICTORIAL_SUMMARY_ROW_SX}>
											<Box component="span" sx={{ fontWeight: "bold" }}>
												Sold Pcs (Period):
											</Box>
											<Box
												component="span"
												title="Total buy pcs − sold in period"
												sx={{ whiteSpace: "normal", lineHeight: 1.35 }}>
												{formatNumber(totals.totalSold)}{" "}
												<Box component="span" sx={PICTORIAL_SUMMARY_BAL_SX}>
													(Bal: {formatNumber((totals.totalBuy || 0) - (totals.totalSold || 0))})
												</Box>
											</Box>
										</Typography>
										<Typography
											variant="body1"
											sx={PICTORIAL_SUMMARY_ROW_SX}>
											<Box component="span" sx={{ fontWeight: "bold" }}>
												Sold Pcs (Overall):
											</Box>
											<Box
												component="span"
												title="Total buy pcs − sold overall"
												sx={{ whiteSpace: "normal", lineHeight: 1.35 }}>
												{formatNumber(totals.totalSoldLifetime || 0)}{" "}
												<Box component="span" sx={PICTORIAL_SUMMARY_BAL_SX}>
													(Bal:{" "}
													{formatNumber((totals.totalBuy || 0) - (totals.totalSoldLifetime || 0))})
												</Box>
											</Box>
										</Typography>
										<Typography
											variant="body1"
											sx={PICTORIAL_SUMMARY_ROW_SX}>
											<Box component="span" sx={{ fontWeight: "bold" }}>
												MRP Value:
											</Box>
											<Box component="span">{formatNumber(totals.soldMRPValue)}</Box>
										</Typography>
										<Typography
											variant="body1"
											sx={PICTORIAL_SUMMARY_ROW_SX}>
											<Box component="span" sx={{ fontWeight: "bold" }}>
												Avg. MRP:
											</Box>
											<Box component="span">{formatNumber(totals.avgMRPSold)}</Box>
										</Typography>
										<Typography
											variant="body1"
											sx={PICTORIAL_SUMMARY_ROW_SX}>
											<Box component="span" sx={{ fontWeight: "bold" }}>
												Avg. ASP:
											</Box>
											<Box component="span">{formatNumber(totals.avgASP)}</Box>
										</Typography>
										<Typography
											variant="body1"
											sx={PICTORIAL_SUMMARY_ROW_SX}>
											<Box component="span" sx={{ fontWeight: "bold" }}>
												COGS Value:
											</Box>
											<Box component="span">{formatNumber(totals.soldCogsValue || 0)}</Box>
										</Typography>
										<Typography
											variant="body1"
											sx={PICTORIAL_SUMMARY_ROW_SX}>
											<Box component="span" sx={{ fontWeight: "bold" }}>
												Multi:
											</Box>
											<Box component="span">{(totals.soldMulti || 0).toFixed(2)}</Box>
										</Typography>
									</Box>

									{/* Third Section - Performance metrics */}
									<Box sx={PICTORIAL_SUMMARY_BOX_SX}>
										<Typography variant="body1" sx={PICTORIAL_SUMMARY_HEADER_SX}>
											PERFORMANCE
										</Typography>
										<Typography
											variant="body1"
											sx={PICTORIAL_SUMMARY_ROW_SX}>
											<Box component="span" sx={{ fontWeight: "bold" }}>
												Sold Period ST%:
											</Box>
											<Box component="span">{calculatePeriodST()}%</Box>
										</Typography>
										<Typography
											variant="body1"
											sx={PICTORIAL_SUMMARY_ROW_SX}>
											<Box component="span" sx={{ fontWeight: "bold" }}>
												Total ST%:
											</Box>
											<Box component="span">{calculateST()}%</Box>
										</Typography>
										<Typography
											variant="body1"
											sx={PICTORIAL_SUMMARY_ROW_SX}>
											<Box component="span" sx={{ fontWeight: "bold" }}>
												Disc%:
											</Box>
											<Box component="span">{discountPctAvg.toFixed(2)}%</Box>
										</Typography>
										<Typography
											variant="body1"
											sx={PICTORIAL_SUMMARY_ROW_SX}>
											<Box component="span" sx={{ fontWeight: "bold" }}>
												Avg. COGS:
											</Box>
											<Box component="span">{formatNumber(totals.avgCOGS)}</Box>
										</Typography>
										<Typography
											variant="body1"
											sx={PICTORIAL_SUMMARY_ROW_SX}>
											<Box component="span" sx={{ fontWeight: "bold" }}>
												Avg. GP%:
											</Box>
											<Box component="span">{formatGPPercentage(totals.gpPercentage)}%</Box>
										</Typography>
										<Typography
											variant="body1"
											sx={{
												...PICTORIAL_SUMMARY_ROW_SX,
												mb: 0,
												cursor: "pointer",
												"&:hover": {
													backgroundColor: "rgba(0, 0, 0, 0.04)",
												},
											}}
											onClick={handleGpContributionClick}
											data-gp-contribution>
											<Box component="span" sx={{ fontWeight: "bold" }}>
												GP Cont/Cat Value:
												{filters.brand &&
													filters.brand.trim() !== "" &&
													selectedItems.brands &&
													selectedItems.brands.size > 1 && (
														<Box
															component="span"
															sx={{
																fontSize: "0.6rem",
																fontWeight: "normal",
																color: "#666",
																ml: 1,
															}}>
															{filters.subCategory && filters.subCategory.trim() !== ""
																? `(100% for ${filters.brand} - ${filters.subCategory})`
																: `(100% for ${filters.brand})`}
														</Box>
													)}
											</Box>
											<Box component="span">{formatGPValue(displayedGpContCatValue)}</Box>
										</Typography>

										<Popover
											open={gpContributionCard.open}
											anchorEl={gpContributionAnchorEl}
											onClose={closeGpContributionCard}
											anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
											transformOrigin={{ vertical: "top", horizontal: "right" }}
											disableRestoreFocus
											slotProps={{
												paper: {
													sx: {
														mt: 0.5,
														width: "min(400px, calc(100vw - 48px))",
														maxHeight: "min(500px, 70vh)",
														bgcolor: "transparent",
														boxShadow: "none",
														overflow: "visible",
													},
												},
											}}>
											<Box
												data-gp-card
												sx={{
													width: "min(400px, calc(100vw - 48px))",
													minHeight: "200px",
													maxHeight: "min(500px, 70vh)",
													display: "flex",
													flexDirection: "column",
												}}>
													<Box
														sx={{
															width: "100%",
															minHeight: "100%",
															backgroundColor: "rgba(0, 0, 0, 0.9)",
															borderRadius: "6px",
															padding: "12px",
															boxShadow: "0 4px 20px rgba(0, 0, 0, 0.3)",
															display: "flex",
															flexDirection: "column",
															transition: "transform 0.3s ease-in-out, opacity 0.3s ease-in-out",
															transform: gpContributionCard.transitioning
																? "translateX(100%)"
																: "translateX(0)",
															opacity: gpContributionCard.transitioning ? 0 : 1,
														}}>
														{/* Header */}
														<Box
															sx={{
																display: "flex",
																alignItems: "center",
																justifyContent: "center",
																position: "relative",
																mb: 2,
																pb: 1.5,
																borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
															}}>
															{(gpContributionCard.viewMode === "brands" ||
																gpContributionCard.viewMode === "categories") && (
																<Button
																	variant="text"
																	size="small"
																	onClick={(e) => {
																		e.stopPropagation();
																		openColorContributionView(
																			gpContributionCard.viewMode === "categories"
																				? "categories"
																				: "brands",
																			gpContributionCard.selectedBrand
																		);
																	}}
																	sx={{
																		position: "absolute",
																		left: 0,
																		top: "50%",
																		transform: "translateY(-50%)",
																		color: "#81c784",
																		fontSize: "0.65rem",
																		minWidth: "auto",
																		py: 0,
																		px: 0.5,
																		textTransform: "none",
																	}}>
																	By color
																</Button>
															)}
															<Box sx={{ textAlign: "center" }}>
																<Typography
																	variant="h6"
																	sx={{
																		color: "white",
																		fontWeight: 600,
																		mb: 0.25,
																		fontSize: "0.9375rem",
																	}}>
																	{gpContributionCard.viewMode === "colors"
																		? "GP Contribution by Color"
																		: gpContributionCard.viewMode === "categories"
																			? "GP Contribution by Category"
																			: "GP Contribution by Brand"}
																</Typography>
																{(gpContributionCard.viewMode === "categories" ||
																	(gpContributionCard.viewMode === "colors" &&
																		gpContributionCard.colorViewFrom === "categories")) &&
																	gpContributionCard.selectedBrand && (
																		<Typography
																			variant="body2"
																			sx={{
																				color: "rgba(255, 255, 255, 0.7)",
																				fontSize: "0.6875rem",
																			}}>
																			Brand: {gpContributionCard.selectedBrand}
																		</Typography>
																	)}
																{gpContributionCard.viewMode === "colors" &&
																	gpContributionCard.colorViewFrom === "brands" && (
																		<Typography
																			variant="body2"
																			sx={{
																				color: "rgba(255, 255, 255, 0.7)",
																				fontSize: "0.6875rem",
																			}}>
																			All loaded products
																		</Typography>
																	)}
															</Box>
															{(gpContributionCard.viewMode === "categories" ||
																gpContributionCard.viewMode === "colors") && (
																<Button
																	variant="outlined"
																	size="small"
																	sx={{
																		position: "absolute",
																		right: 0,
																		top: "50%",
																		transform: "translateY(-50%)",
																		color: "#FFFFFF",
																		borderColor: "#FFFFFF",
																		minWidth: "auto",
																		width: 32,
																		height: 32,
																		p: 0,
																		borderRadius: "50%",
																		"&:hover": {
																			backgroundColor: "rgba(76, 175, 80, 0.1)",
																			borderColor: "#FFFFFF",
																		},
																	}}
																	onClick={
																		gpContributionCard.viewMode === "colors"
																			? handleBackFromColors
																			: handleBackToBrands
																	}>
																	←
																</Button>
															)}
														</Box>

														{/* Summary Stats */}
														{gpContributionCard.viewMode === "categories" &&
															gpContributionCard.categoryContributions.length > 0 && (
																<Box
																	sx={{
																		display: "flex",
																		gap: 1.5,
																		mb: 2,
																		flexWrap: "wrap",
																	}}>
																	<Chip
																		label={`${gpContributionCard.categoryContributions.length} Categories`}
																		size="small"
																		sx={{
																			backgroundColor: "rgba(76, 175, 80, 0.2)",
																			color: "#FFFFFF",
																			fontWeight: 600,
																		}}
																	/>
																	<Chip
																		label={`Total: ${formatGPValue(
																			gpContributionCard.categoryContributions.reduce(
																				(sum, cat) => sum + cat.gpValue,
																				0
																			)
																		)}`}
																		size="small"
																		sx={{
																			backgroundColor: "rgba(255, 255, 255, 0.1)",
																			color: "white",
																			fontWeight: 600,
																		}}
																	/>
																</Box>
															)}

														{gpContributionCard.viewMode === "colors" &&
															(gpContributionCard.colorContributions.length > 0 ||
																gpContributionCard.colorContributionsTinw.length > 0 ||
																gpContributionCard.colorContributionsSoh.length > 0) && (
																<Box
																	sx={{
																		display: "flex",
																		gap: 1.5,
																		mb: 2,
																		flexWrap: "wrap",
																	}}>
																	{gpContributionCard.colorContributions.length > 0 && (
																		<>
																			<Chip
																				label={`${gpContributionCard.colorContributions.length} Colors (sold)`}
																				size="small"
																				sx={{
																					backgroundColor: "rgba(129, 199, 132, 0.25)",
																					color: "#FFFFFF",
																					fontWeight: 600,
																				}}
																			/>
																			<Chip
																				label={`Sold pcs: ${formatNumber(
																					gpContributionCard.colorContributions.reduce(
																						(sum, row) => sum + (row.soldQty || 0),
																						0
																					)
																				)}`}
																				size="small"
																				sx={{
																					backgroundColor: "rgba(255, 255, 255, 0.1)",
																					color: "white",
																					fontWeight: 600,
																				}}
																			/>
																		</>
																	)}
																	{gpContributionCard.colorContributionsTinw.length > 0 && (
																		<Chip
																			label={`T.INW pcs: ${formatNumber(
																				gpContributionCard.colorContributionsTinw.reduce(
																					(sum, row) => sum + (row.soldQty || 0),
																					0
																				)
																			)}`}
																			size="small"
																			sx={{
																				backgroundColor: "rgba(255, 255, 255, 0.1)",
																				color: "white",
																				fontWeight: 600,
																			}}
																		/>
																	)}
																	{gpContributionCard.colorContributionsSoh.length > 0 && (
																		<Chip
																			label={`SOH pcs: ${formatNumber(
																				gpContributionCard.colorContributionsSoh.reduce(
																					(sum, row) => sum + (row.soldQty || 0),
																					0
																				)
																			)}`}
																			size="small"
																			sx={{
																				backgroundColor: "rgba(255, 255, 255, 0.1)",
																				color: "white",
																				fontWeight: 600,
																			}}
																		/>
																	)}
																</Box>
															)}

														{/* Content */}
														<Box
															sx={{
																flex: "1 1 auto",
																px: 0.5,
																display: "flex",
																flexDirection: "column",
																overflow: "hidden",
															}}>
															<Box
																sx={{
																	flex: "1 1 auto",
																	minHeight: "150px",
																	maxHeight:
																		gpContributionCard.viewMode === "colors"
																			? "none"
																			: "300px",
																	overflowY:
																		gpContributionCard.viewMode === "colors"
																			? "visible"
																			: "auto",
																	overflowX: "hidden",
																	"&::-webkit-scrollbar": {
																		width: "1px",
																	},
																	"&::-webkit-scrollbar-track": {
																		background: "transparent",
																		margin: "0",
																	},
																	"&::-webkit-scrollbar-thumb": {
																		background: "rgba(255, 255, 255, 0.2)",
																		borderRadius: "0.5px",
																		border: "none",
																		"&:hover": {
																			background: "rgba(255, 255, 255, 0.4)",
																		},
																	},
																	"&::-webkit-scrollbar-thumb:hover": {
																		background: "rgba(255, 255, 255, 0.4)",
																	},
																	// Firefox scrollbar styling
																	scrollbarWidth: "thin",
																	scrollbarColor: "rgba(255, 255, 255, 0.2) transparent",
																}}>
																{gpContributionCard.viewMode === "colors" ? (
																	gpContributionCard.colorContributions.length > 0 ||
																	gpContributionCard.colorContributionsTinw.length > 0 ||
																	gpContributionCard.colorContributionsSoh.length > 0 ? (
																		<Box
																			sx={{
																				py: 0.5,
																				textAlign: "left",
																				display: "flex",
																				flexDirection: "column",
																				alignItems: "stretch",
																				gap: 1.5,
																				width: "100%",
																			}}>
																			{gpContributionCard.colorContributionsTinw.length > 0 && (
																				<Box sx={{ width: "100%", minWidth: 0 }}>
																					<ColorContributionRamp
																						rows={gpContributionCard.colorContributionsTinw}
																						title="COLOUR · T.INW %"
																						amountLabel="T.INW"
																						variant="dark"
																						barHeight={30}
																						showSegmentContribution
																						contributionLabel="COGS"
																						onSegmentClick={(row) => handleRampSegmentClick(row, "color")}
																						selectedFilterKey={rampColorSelectedKey}
																					/>
																				</Box>
																			)}
																			{gpContributionCard.colorContributions.length > 0 && (
																				<Box sx={{ width: "100%", minWidth: 0 }}>
																					<ColorContributionRamp
																						rows={gpContributionCard.colorContributions}
																						title="COLOUR · PERIOD SOLD QTY %"
																						amountLabel="sold qty"
																						variant="dark"
																						barHeight={30}
																						showSegmentGp
																						onSegmentClick={(row) => handleRampSegmentClick(row, "color")}
																						selectedFilterKey={rampColorSelectedKey}
																					/>
																				</Box>
																			)}
																			{gpContributionCard.colorContributionsSoh.length > 0 && (
																				<Box sx={{ width: "100%", minWidth: 0 }}>
																					<ColorContributionRamp
																						rows={gpContributionCard.colorContributionsSoh}
																						title="COLOUR · SOH %"
																						amountLabel="SOH"
																						variant="dark"
																						barHeight={30}
																						sohWarehouseSplit
																						onSegmentClick={(row) => handleRampSegmentClick(row, "color")}
																						selectedFilterKey={rampColorSelectedKey}
																					/>
																				</Box>
																			)}
																		</Box>
																	) : (
																		<Box sx={{ textAlign: "center", py: 4 }}>
																			<Typography
																				variant="body1"
																				sx={{ color: "rgba(255, 255, 255, 0.7)", mb: 1 }}>
																				No colour breakdown in this view — need positive T.INW,
																				sold qty, or SOH by colour in the loaded slice.
																			</Typography>
																		</Box>
																	)
																) : gpContributionCard.viewMode === "categories" ? (
																	// Show category contributions for selected brand
																	gpContributionCard.categoryContributions.length > 0 ? (
																		<List
																			sx={{
																				p: 0,
																				flex: "1 1 auto",
																				display: "flex",
																				flexDirection: "column",
																				gap: 0.5,
																			}}>
																			{gpContributionCard.categoryContributions.map(
																				(category, index) => (
																					<ListItem
																						key={category.category}
																						sx={{
																							px: 0,
																							py: 0.5,
																							borderRadius: 0.5,
																							mb: 0.25,
																							cursor: "default",
																						}}>
																						<ListItemText
																							primary={
																								<Box
																									sx={{
																										display: "flex",
																										alignItems: "center",
																										justifyContent:
																											"space-between",
																									}}>
																									<Typography
																										variant="body2"
																										sx={{
																											color: "white",
																											fontWeight: 500,
																											fontSize: "0.6875rem",
																										}}>
																										{category.category}
																									</Typography>
																									<Box
																										sx={{
																											display: "flex",
																											alignItems: "center",
																											gap: 1,
																										}}>
																										<Typography
																											variant="body2"
																											sx={{
																												color: "#FFFFFF",
																												fontWeight: 600,
																												fontSize: "0.6875rem",
																											}}>
																											{formatGPValue(
																												category.gpValue
																											)}
																										</Typography>
																										<Chip
																											label={`${category.percentage.toFixed(
																												1
																											)}%`}
																											size="small"
																											sx={{
																												backgroundColor:
																													"rgba(76, 175, 80, 0.2)",
																												color: "#FFFFFF",
																												fontWeight: 600,
																												fontSize: "0.6rem",
																												height: 20,
																												"& .MuiChip-label": {
																													px: 0.5,
																												},
																											}}
																										/>
																									</Box>
																								</Box>
																							}
																							secondary={
																								<Box sx={{ mt: 0.25 }}>
																									<LinearProgress
																										variant="determinate"
																										value={category.percentage}
																										sx={{
																											height: 3,
																											borderRadius: 1.5,
																											backgroundColor:
																												"rgba(255, 255, 255, 0.1)",
																											"& .MuiLinearProgress-bar":
																												{
																													backgroundColor:
																														"#FFFFFF",
																													borderRadius: 2,
																												},
																										}}
																									/>
																								</Box>
																							}
																						/>
																					</ListItem>
																				)
																			)}
																		</List>
																	) : (
																		<Box sx={{ textAlign: "center", py: 4 }}>
																			<Typography
																				variant="body1"
																				sx={{ color: "rgba(255, 255, 255, 0.7)", mb: 1 }}>
																				No categories found for
																			</Typography>
																			<Typography
																				variant="h6"
																				sx={{ color: "white", fontWeight: 600 }}>
																				{gpContributionCard.selectedBrand}
																			</Typography>
																		</Box>
																	)
																) : (
																	// Show brand contributions
																	<List
																		sx={{
																			p: 0,
																			flex: "1 1 auto",
																			display: "flex",
																			flexDirection: "column",
																			gap: 0.5,
																		}}>
																		{gpContributionCard.brandContributions.map((brand, index) => (
																			<ListItem
																				key={brand.brand}
																				sx={{
																					px: 0,
																					py: 0.5,
																					borderRadius: 0.5,
																					mb: 0.25,
																					"&:hover": {
																						backgroundColor: "rgba(255, 255, 255, 0.1)",
																						cursor: "pointer",
																					},
																					transition: "background-color 0.2s ease",
																				}}
																				onClick={() => handleBrandClick(brand.brand)}>
																				<ListItemText
																					primary={
																						<Box
																							sx={{
																								display: "flex",
																								alignItems: "center",
																								justifyContent: "space-between",
																							}}>
																							<Typography
																								variant="body2"
																								sx={{
																									color: "white",
																									fontWeight: 500,
																									fontSize: "0.6875rem",
																								}}>
																								{brand.brand}
																							</Typography>
																							<Box
																								sx={{
																									display: "flex",
																									alignItems: "center",
																									gap: 1,
																								}}>
																								<Typography
																									variant="body2"
																									sx={{
																										color: "#FFFFFF",
																										fontWeight: 600,
																										fontSize: "0.6875rem",
																									}}>
																									{formatGPValue(brand.gpValue)}
																								</Typography>
																								<Chip
																									label={`${brand.percentage.toFixed(
																										1
																									)}%`}
																									size="small"
																									sx={{
																										backgroundColor:
																											"rgba(76, 175, 80, 0.2)",
																										color: "#FFFFFF",
																										fontWeight: 600,
																										fontSize: "0.6rem",
																										height: 20,
																										"& .MuiChip-label": {
																											px: 0.5,
																										},
																									}}
																								/>
																							</Box>
																						</Box>
																					}
																					secondary={
																						<Box sx={{ mt: 0.25 }}>
																							<LinearProgress
																								variant="determinate"
																								value={brand.percentage}
																								sx={{
																									height: 3,
																									borderRadius: 1.5,
																									backgroundColor:
																										"rgba(255, 255, 255, 0.1)",
																									"& .MuiLinearProgress-bar": {
																										backgroundColor: "#FFFFFF",
																										borderRadius: 2,
																									},
																								}}
																							/>
																						</Box>
																					}
																				/>
																			</ListItem>
																		))}
																	</List>
																)}
															</Box>
														</Box>
													</Box>
												</Box>
										</Popover>
									</Box>
								</Box>
							</Grid>

							{/* Filters Row — horizontal */}
							{filtersExpanded && (
								<Grid item xs={12}>
									<Box sx={{ mb: 2, position: "relative" }}>
										{error && (
											<Alert severity="warning" sx={{ mb: 2 }} onClose={() => setError("")}>
												{error}
											</Alert>
										)}
										<Box
											sx={{
												backgroundColor: "#fafafa",
												borderRadius: "12px",
												padding: { xs: "16px", sm: "20px", md: "24px" },
												border: "1px solid #f0f0f0",
											}}>
											<LocalizationProvider dateAdapter={AdapterDateFns}>
												<Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
													<Grid item xs={12} sm={6} md={3}>
														<DatePicker
															label="Start Date"
															value={pendingDateRange.startDate}
															onChange={handleStartDateChange}
															slotProps={{ textField: { size: "small", fullWidth: true } }}
															format="dd-MM-yyyy"
														/>
													</Grid>
													<Grid item xs={12} sm={6} md={3}>
														<DatePicker
															label="End Date"
															value={pendingDateRange.endDate}
															onChange={handleEndDateChange}
															minDate={pendingDateRange.startDate}
															slotProps={{ textField: { size: "small", fullWidth: true } }}
															format="dd-MM-yyyy"
														/>
													</Grid>
													<Grid item xs={12} md={6}>
														<Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
															<Button variant="contained" onClick={applyFilters} disabled={loading} size="small">
																Apply
															</Button>
															<Button variant="outlined" onClick={handleResetFilters} size="small" sx={{ minWidth: "40px", p: 0 }}>
																<RefreshIcon fontSize="small" />
															</Button>
															{!loading && products.length > 0 && (
																<Typography variant="caption" sx={{ color: "#666", fontSize: "0.6875rem", fontWeight: 500 }}>
																	{pagination.total} results
																</Typography>
															)}
														</Box>
													</Grid>
												</Grid>
											</LocalizationProvider>
											<Grid container spacing={2}>
												<Grid item xs={6} sm={4} md={1.5}>{createDropdownFilter("brand", "Brand")}</Grid>
												<Grid item xs={6} sm={4} md={1.5}>{createDropdownFilter("subBrand", "Sub Brand")}</Grid>
												<Grid item xs={6} sm={4} md={1.5}>{createDropdownFilter("department", "Department")}</Grid>
												<Grid item xs={6} sm={4} md={1.5}>{createDropdownFilter("subCategory", "Sub Cat.")}</Grid>
												<Grid item xs={6} sm={4} md={1.5}>{createDropdownFilter("style", "Style")}</Grid>
												<Grid item xs={6} sm={4} md={1.5}>{createDropdownFilter("color", "Color")}</Grid>
												<Grid item xs={6} sm={4} md={1.5}>{createDropdownFilter("sleeve", "Sleeve")}</Grid>
												<Grid item xs={6} sm={4} md={1.5}>{createDropdownFilter("season", "Season")}</Grid>
												<Grid item xs={6} sm={4} md={1.5}>{createDropdownFilter("division", "Division")}</Grid>
												<Grid item xs={6} sm={4} md={1.5}>{createDropdownFilter("fashionability", "Fashionability")}</Grid>
												<Grid item xs={6} sm={4} md={1.5}>{createDropdownFilter("occasion", "Occasion")}</Grid>
												<Grid item xs={6} sm={4} md={1.5}>{createDropdownFilter("palette", "Palettes")}</Grid>
												<Grid item xs={6} sm={4} md={1.5}>{createDropdownFilter("fitType", "Fit Type")}</Grid>
												<Grid item xs={6} sm={4} md={1.5}>{createDropdownFilter("fabricBlend", "Blend")}</Grid>
												<Grid item xs={6} sm={4} md={1.5}>{createDropdownFilter("nod", "NOD")}</Grid>
												<Grid item xs={6} sm={4} md={1.5}>{createDropdownFilter("channel", "Channel")}</Grid>
												<Grid item xs={6} sm={4} md={1.5}>{createDropdownFilter("store", "Store")}</Grid>
												<Grid item xs={6} sm={4} md={1.5}>{createDropdownFilter("city", "City")}</Grid>
												<Grid item xs={6} sm={4} md={1.5}>{createDropdownFilter("aging", "Aging")}</Grid>
												<Grid item xs={6} sm={4} md={1.5}>{createDropdownFilter("st_percentage", "ST%")}</Grid>
												<Grid item xs={6} sm={4} md={1.5}>{createDropdownFilter("region", "Region")}</Grid>
												<Grid item xs={6} sm={4} md={1.5}>{createDropdownFilter("tier", "Tier")}</Grid>
											</Grid>
										</Box>
										{filterOptionsLoading && (
											<Box
												sx={{
													position: "absolute",
													top: 0,
													left: 0,
													right: 0,
													bottom: 0,
													bgcolor: "rgba(255,255,255,0.6)",
													zIndex: 10,
													display: "flex",
													alignItems: "center",
													justifyContent: "center",
													borderRadius: "12px",
												}}>
												<CircularProgress size={32} />
											</Box>
										)}
									</Box>
								</Grid>
							)}

							{/* Full-width colour ramps — T.INW first, then sold qty (GP share on sold ramp via showSegmentGp) */}
							{(colorContributionRowsLoaded.length > 0 ||
								colorContributionRowsOverallLoaded.length > 0 ||
								colorContributionRowsTinwLoaded.length > 0 ||
								colorContributionRowsSohLoaded.length > 0 ||
								fashionabilityContributionRowsLoaded.length > 0 ||
								fashionabilityContributionRowsOverallLoaded.length > 0) && (
								<Grid item xs={12} sx={{ mt: { xs: 2, md: 1 } }}>
									<Box
										sx={{
											width: "100%",
											display: "flex",
											flexDirection: "column",
											alignItems: "stretch",
											gap: { xs: 2, sm: 2.5, md: 3 },
										}}
										onClick={(e) => e.stopPropagation()}>
										{colorContributionRowsTinwLoaded.length > 0 && (
											<Box sx={{ width: "100%" }}>
												<ColorContributionRamp
													rows={colorContributionRowsTinwLoaded}
													title="COLOUR · T.INW %"
													amountLabel="T.INW"
													variant="light"
													showSegmentContribution
													contributionLabel="COGS"
													onSegmentClick={(row) => handleRampSegmentClick(row, "color")}
													selectedFilterKey={rampColorSelectedKey}
												/>
											</Box>
										)}
										{colorContributionRowsLoaded.length > 0 && (
											<Box sx={{ width: "100%" }}>
												<ColorContributionRamp
													rows={colorContributionRowsLoaded}
													title="COLOUR · PERIOD SOLD QTY %"
													amountLabel="sold qty"
													variant="light"
													showSegmentGp
													onSegmentClick={(row) => handleRampSegmentClick(row, "color")}
													selectedFilterKey={rampColorSelectedKey}
												/>
											</Box>
										)}
										{colorContributionRowsOverallLoaded.length > 0 && (
											<Box sx={{ width: "100%" }}>
												<ColorContributionRamp
													rows={colorContributionRowsOverallLoaded}
													title="COLOUR · OVERALL SOLD QTY %"
													amountLabel="overall sold qty"
													variant="light"
													showSegmentGp
													onSegmentClick={(row) => handleRampSegmentClick(row, "color_overall")}
													selectedFilterKey={rampColorOverallSelectedKey}
												/>
											</Box>
										)}
										{fashionabilityContributionRowsLoaded.length > 0 && (
											<Box sx={{ width: "100%" }}>
												<FashionabilityContributionRamp
													rows={fashionabilityContributionRowsLoaded}
													title="FASHIONABILITY · PERIOD SOLD QTY %"
													amountLabel="sold qty"
													onSegmentClick={(row) => handleRampSegmentClick(row, "fashionability")}
													selectedFilterKey={rampFashionabilitySelectedKey}
												/>
											</Box>
										)}
										{fashionabilityContributionRowsOverallLoaded.length > 0 && (
											<Box sx={{ width: "100%" }}>
												<FashionabilityContributionRamp
													rows={fashionabilityContributionRowsOverallLoaded}
													title="FASHIONABILITY · OVERALL SOLD QTY %"
													amountLabel="overall sold qty"
													onSegmentClick={(row) =>
														handleRampSegmentClick(row, "fashionability_overall")
													}
													selectedFilterKey={rampFashionabilityOverallSelectedKey}
												/>
											</Box>
										)}
										{colorContributionRowsSohLoaded.length > 0 && (
											<Box sx={{ width: "100%" }}>
												<ColorContributionRamp
													rows={colorContributionRowsSohLoaded}
													title="COLOUR · SOH %"
													amountLabel="SOH"
													variant="light"
													sohWarehouseSplit
													onSegmentClick={(row) => handleRampSegmentClick(row, "color")}
													selectedFilterKey={rampColorSelectedKey}
												/>
											</Box>
										)}
										{rampColorDrilldown && (
											<Box sx={{ mt: 1, display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
												<Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
													Showing grid:
												</Typography>
												<Chip
													label={
														rampColorDrilldown.dimension === "fashionability"
															? `Fashionability: ${rampColorDrilldown.label}`
															: rampColorDrilldown.dimension === "fashionability_overall"
																? `Fashionability (overall): ${rampColorDrilldown.label}`
																: rampColorDrilldown.dimension === "color_overall"
																	? `Colour (overall): ${rampColorDrilldown.label}`
																	: rampColorDrilldown.label
													}
													onDelete={() => setRampColorDrilldown(null)}
													size="small"
													color="primary"
													variant="outlined"
												/>
											</Box>
										)}
									</Box>
								</Grid>
							)}
						</Grid>

						{/* Enhanced Filters Section */}
						{/* Controls Row - Sort, actions and Download on same line */}
						{filtersExpanded && (
							<Box
								sx={{
									mb: 3,
									mt: 4,
									"& .MuiButton-root": { fontSize: "0.6875rem", py: 0.25, px: 1, minWidth: "auto" },
									"& .MuiButton-startIcon": { mr: 0.25 },
									"& .MuiButton-endIcon": { ml: 0.25 },
									"& .controls-right .MuiButton-root": { fontSize: "0.75rem", py: 0.5, px: 1.5 },
								}}>
								<Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center" justifyContent="space-between" width="100%">
									<Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ flex: 1, minWidth: 0 }}>
											{renderSortButton("period_st", "Period ST")}
											{renderSortButton("st", "Total ST")}
											{renderSortButton("sold_in_period", "Period Sold")}
											{renderSortButton("total_sold", "Total Sold")}
											{renderSortButton("inw_qty", "Inw Qty")}
											{renderSortButton("cogs", "COGS")}
											{renderSortButton("multi_mrp_cogs", "Multi")}
											{renderSortButton("gp_cont", "GP Cont")}
											{renderSortButton("period_ros", "Period Wgt. ROS")}
											{renderSortButton("ros", "Wgt. ROS")}
											{renderSortButton("doh", "DOH")}
											{renderSortButton("aging", "Aging")}
											{renderSortButton("max_recv_aging", "Last Aging")}
											<Button
												variant="outlined"
												onClick={handleResetFilters}
												size="small"
												startIcon={<RefreshIcon />}
												sx={{
													borderColor: "#ff4444",
													color: "#ff4444",
													"&:hover": {
														borderColor: "#ff0000",
														backgroundColor: "rgba(255, 0, 0, 0.04)",
													},
												}}>
												Reset
											</Button>
									</Stack>
								</Stack>
							</Box>
						)}


						{/* Loading Indicator */}
						{loading && <LoadingOverlay />}

						{/* Result Count Display */}
						{!loading && products.length > 0 && (
							<Box sx={{ mb: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
								<Typography
									variant="body1"
									sx={{
										fontWeight: 500,
										color: "#666",
										fontSize: "0.8125rem",
									}}>
									Showing {products.length} of {pagination.total} products
								</Typography>
								<Typography
									variant="body2"
									sx={{
										color: "#999",
										fontSize: "0.6875rem",
									}}>
									Page {pagination.page} of {pagination.total_pages}
								</Typography>
							</Box>
						)}

						{/* No Results Message - Only show when filters are applied */}
						{!loading && products.length === 0 && hasActiveFilters() && (
							<Box
								sx={{
									textAlign: "center",
									py: 8,
									px: 4,
									border: "2px dashed #e0e0e0",
									borderRadius: 2,
									backgroundColor: "#fafafa",
								}}>
								<Typography
									variant="h6"
									sx={{
										color: "#666",
										mb: 1,
										fontWeight: 500,
									}}>
									No products found
								</Typography>
								<Typography
									variant="body2"
									sx={{
										color: "#999",
										mb: 2,
									}}>
									Try adjusting your filters to see more results
								</Typography>
								<Button
									variant="outlined"
									size="small"
									onClick={() => {
										// Reset all filters
										const resetFilters = {
											department: "",
											subCategory: "",
											brand: "",
											subBrand: "",
											style: "",
											color: "",
											sleeve: "",
											division: "",
											season: "",
											fashionability: "",
											occasion: "",
											palette: "",
											fitType: "",
											fabricBlend: "",
											nod: "",
											channel: "",
											store: "",
											city: "",
											state: "",
											region: "",
											tier: "",
											aging: "",
											st_percentage: "",
											start_date: "",
											end_date: "",
										};
										setRampColorDrilldown(null);
										setFilters(resetFilters);
										setPendingFilters(resetFilters);
										applyFilters();
									}}
									sx={{
										textTransform: "none",
										borderColor: "#ddd",
										color: "#666",
										"&:hover": {
											borderColor: "#999",
											backgroundColor: "#f5f5f5",
										},
									}}>
									Clear all filters
								</Button>
							</Box>
						)}

					{/* Pivotal / full-size quick-filter bars — horizontal */}
					{!loading && products.length > 0 && (
						<Box
							sx={{
								display: "flex",
								alignItems: "flex-end",
								gap: 2,
								mb: 1.5,
								flexWrap: "wrap",
								rowGap: 1,
							}}>
							<Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, minWidth: 0 }}>
								<Typography variant="body2" sx={{ fontSize: "0.72rem", color: "#555", fontWeight: 600, whiteSpace: "nowrap" }}>
									Pivotal Broken:
								</Typography>
								<ToggleButtonGroup
									value={brokenFilter}
									exclusive
									onChange={handleBrokenFilterChange}
									size="small"
									sx={{ "& .MuiToggleButton-root": { textTransform: "none", fontSize: "0.7rem", py: 0.4, px: 1.2, lineHeight: 1.4 } }}>
									<ToggleButton
										value="WH"
										sx={{
											borderColor: "#1565c0",
											color: "#1565c0",
											"&.Mui-selected": { bgcolor: "#1565c0", color: "#fff", "&:hover": { bgcolor: "#0d47a1" } },
											"&:hover": { bgcolor: "#e3f2fd" },
										}}>
										⚠ WH Broken
										{whBrokenCount > 0 && (
											<Box component="span" sx={{ ml: 0.75, bgcolor: brokenFilter === "WH" ? "rgba(255,255,255,0.3)" : "#1565c0", color: "#fff", fontSize: "0.62rem", fontWeight: 700, borderRadius: 3, px: 0.6, py: 0.05 }}>
												{whBrokenCount}
											</Box>
										)}
									</ToggleButton>
									<ToggleButton
										value="EBO"
										sx={{
											borderColor: "#c62828",
											color: "#c62828",
											"&.Mui-selected": { bgcolor: "#c62828", color: "#fff", "&:hover": { bgcolor: "#b71c1c" } },
											"&:hover": { bgcolor: "#ffebee" },
										}}>
										⚠ EBO Broken
										{eboBrokenCount > 0 && (
											<Box component="span" sx={{ ml: 0.75, bgcolor: brokenFilter === "EBO" ? "rgba(255,255,255,0.3)" : "#c62828", color: "#fff", fontSize: "0.62rem", fontWeight: 700, borderRadius: 3, px: 0.6, py: 0.05 }}>
												{eboBrokenCount}
											</Box>
										)}
									</ToggleButton>
								</ToggleButtonGroup>
							</Box>

							<Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, minWidth: 0 }}>
								<Typography variant="body2" sx={{ fontSize: "0.72rem", color: "#555", fontWeight: 600, whiteSpace: "nowrap" }}>
									Pivotal Available:
								</Typography>
								<ToggleButtonGroup
									value={availableFilter}
									exclusive
									onChange={handleAvailableFilterChange}
									size="small"
									sx={{ "& .MuiToggleButton-root": { textTransform: "none", fontSize: "0.7rem", py: 0.4, px: 1.2, lineHeight: 1.4 } }}>
									<ToggleButton
										value="WH"
										sx={{
											borderColor: "#2e7d32",
											color: "#2e7d32",
											"&.Mui-selected": { bgcolor: "#2e7d32", color: "#fff", "&:hover": { bgcolor: "#1b5e20" } },
											"&:hover": { bgcolor: "#e8f5e9" },
										}}>
										✓ WH Available
										{whAvailableCount > 0 && (
											<Box component="span" sx={{ ml: 0.75, bgcolor: availableFilter === "WH" ? "rgba(255,255,255,0.3)" : "#2e7d32", color: "#fff", fontSize: "0.62rem", fontWeight: 700, borderRadius: 3, px: 0.6, py: 0.05 }}>
												{whAvailableCount}
											</Box>
										)}
									</ToggleButton>
									<ToggleButton
										value="EBO"
										sx={{
											borderColor: "#00695c",
											color: "#00695c",
											"&.Mui-selected": { bgcolor: "#00695c", color: "#fff", "&:hover": { bgcolor: "#004d40" } },
											"&:hover": { bgcolor: "#e0f2f1" },
										}}>
										✓ EBO Available
										{eboAvailableCount > 0 && (
											<Box component="span" sx={{ ml: 0.75, bgcolor: availableFilter === "EBO" ? "rgba(255,255,255,0.3)" : "#00695c", color: "#fff", fontSize: "0.62rem", fontWeight: 700, borderRadius: 3, px: 0.6, py: 0.05 }}>
												{eboAvailableCount}
											</Box>
										)}
									</ToggleButton>
								</ToggleButtonGroup>
							</Box>

							<Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, minWidth: 0 }}>
								<Typography variant="body2" sx={{ fontSize: "0.72rem", color: "#555", fontWeight: 600, whiteSpace: "nowrap" }}>
									Full Size Set:
								</Typography>
								<ToggleButtonGroup
									value={fullSizeSetFilter}
									exclusive
									onChange={handleFullSizeSetFilterChange}
									size="small"
									sx={{ "& .MuiToggleButton-root": { textTransform: "none", fontSize: "0.7rem", py: 0.4, px: 1.2, lineHeight: 1.4 } }}>
									<ToggleButton
										value="WH"
										sx={{
											borderColor: "#6a1b9a",
											color: "#6a1b9a",
											"&.Mui-selected": { bgcolor: "#6a1b9a", color: "#fff", "&:hover": { bgcolor: "#4a148c" } },
											"&:hover": { bgcolor: "#f3e5f5" },
										}}>
										✓ WH Full Set
										{whFullSizeSetCount > 0 && (
											<Box component="span" sx={{ ml: 0.75, bgcolor: fullSizeSetFilter === "WH" ? "rgba(255,255,255,0.3)" : "#6a1b9a", color: "#fff", fontSize: "0.62rem", fontWeight: 700, borderRadius: 3, px: 0.6, py: 0.05 }}>
												{whFullSizeSetCount}
											</Box>
										)}
									</ToggleButton>
									<ToggleButton
										value="EBO"
										sx={{
											borderColor: "#e65100",
											color: "#e65100",
											"&.Mui-selected": { bgcolor: "#e65100", color: "#fff", "&:hover": { bgcolor: "#bf360c" } },
											"&:hover": { bgcolor: "#fff3e0" },
										}}>
										✓ EBO Full Set
										{eboFullSizeSetCount > 0 && (
											<Box component="span" sx={{ ml: 0.75, bgcolor: fullSizeSetFilter === "EBO" ? "rgba(255,255,255,0.3)" : "#e65100", color: "#fff", fontSize: "0.62rem", fontWeight: 700, borderRadius: 3, px: 0.6, py: 0.05 }}>
												{eboFullSizeSetCount}
											</Box>
										)}
									</ToggleButton>
								</ToggleButtonGroup>
							</Box>

							{activeQuickFilter && (
								<Typography variant="body2" sx={{ fontSize: "0.68rem", color: "#888", pb: 0.5, ml: "auto" }}>
									Showing {paginationSourceList.length} of {allProductsForView.length} options
									{quickFilterExportLabel ? ` · Export uses: ${quickFilterExportLabel}` : ""}
								</Typography>
							)}
						</Box>
					)}

					{/* Products Grid - This division will be captured for PDF */}
					<Box ref={pdfRef} sx={{ position: "relative" }}>
						<Fade in={!loading} timeout={120}>
							<Grid container spacing={1.5} justifyContent="flex-start" alignItems="flex-start" sx={{ minHeight: "400px" }}>
								{products.map((product, index) => (
									<Grid
										item
										key={`${product.PKEY}-${pagination.page}-${index}`}
										id={`pict-card-${String(product.PKEY).replace(/[^a-zA-Z0-9]/g, "-")}`}
										xs={12} sm={6} md={4} lg={3} xl={2.4}
										sx={{ scrollMarginTop: "80px", minWidth: 0 }}>
										<ProductCard
									product={product}
									metricsChannel={metricsChannel}
									allProducts={allProductsForView}
									onNavigateToProduct={navigateToProduct}
								/>
									</Grid>
								))}
								</Grid>
							</Fade>
							{rampGridSelectionOverlay && (
								<Box
									aria-hidden
									sx={{
										position: "absolute",
										inset: 0,
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										backgroundColor: "rgba(255, 255, 255, 0.58)",
										backdropFilter: "blur(2px)",
										zIndex: 2,
										borderRadius: 1,
										pointerEvents: "none",
									}}>
									<CircularProgress size={36} thickness={4} sx={{ color: "primary.main" }} />
								</Box>
							)}
						</Box>

						<Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
							<Pagination
								count={pagination.total_pages}
								page={pagination.page}
								onChange={handlePageChange}
								color="primary"
								size="large"
								siblingCount={2}
								showFirstButton
								showLastButton
							/>
						</Box>
					</Paper>
				)}
			</Container>

			<Dialog
				open={pdfFieldPickerOpen}
				onClose={handlePdfFieldPickerCancel}
				maxWidth="sm"
				fullWidth
				aria-labelledby="pdf-field-picker-title">
				<DialogTitle id="pdf-field-picker-title">
					{exportPickerFormat === "csv" ? "Export fields (CSV)" : "Export fields (PDF)"}
					<Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontWeight: 400 }}>
						{pdfFieldPickerScope === "current"
							? "Current page — choose metrics to include."
							: "All pages — same choices apply to every page."}
						{quickFilterExportLabel
							? ` Only products matching “${quickFilterExportLabel}” are exported.`
							: ""}
					</Typography>
				</DialogTitle>
				<DialogContent dividers>
					<Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
						{exportPickerFormat === "pdf"
							? "Page 1 is the cover (totals and applied filters). Product tiles start on page 2. Summary header applies to CSV only."
							: "Summary header adds Buy/Sold totals as leading rows. Product columns follow your selections."}
					</Typography>
					<Stack direction="row" spacing={1} sx={{ mb: 1.5, flexWrap: "wrap" }}>
						<Button size="small" variant="outlined" onClick={handlePdfFieldSelectAll}>
							Select all
						</Button>
						<Button size="small" variant="outlined" onClick={handlePdfFieldClearAll}>
							Clear all
						</Button>
					</Stack>
					<FormGroup sx={{ gap: 0.25 }}>
						{PICTORIAL_EXPORT_FIELDS.map(({ id, label }) => (
							<FormControlLabel
								key={id}
								control={
									<Checkbox
										size="small"
										checked={Boolean(pdfFieldSelection[id])}
										onChange={() => handlePdfFieldToggle(id)}
									/>
								}
								label={<Typography variant="body2">{label}</Typography>}
							/>
						))}
					</FormGroup>
				</DialogContent>
				<DialogActions sx={{ px: 3, pb: 2 }}>
					<Button onClick={handlePdfFieldPickerCancel} color="inherit">
						Cancel
					</Button>
					<Button variant="contained" onClick={handlePdfFieldPickerExport}>
						{exportPickerFormat === "csv" ? "Download CSV" : "Download PDF"}
					</Button>
				</DialogActions>
			</Dialog>

			<Dialog
				open={uploadModalOpen}
				onClose={closeUploadModal}
				maxWidth="lg"
				fullWidth
				scroll="paper"
				PaperProps={{ sx: { maxHeight: "90vh" } }}>
				<UploadPage
					dateRange={dateRange}
					onBack={closeUploadModal}
					onApplyFilters={(filters, dateRangeToApply) => {
						handleApplyFiltersFromUpload(filters, dateRangeToApply);
						closeUploadModal();
					}}
				/>
			</Dialog>
		</ThemeProvider>
	);
};

export default PictorialST;
